<template>
  <div
    class="workflow-app-form"
    :aria-busy="isLoading ? 'true' : 'false'"
    aria-live="polite"
  >
    <Transition name="fade">
      <div v-if="isLoading" class="form-loading-overlay">
        <ProgressSpinner
          class="form-loading-spinner"
          stroke-width="4"
          animation-duration="1s"
        />
        <p class="form-loading-text">{{ $t('g.loading') }}</p>
      </div>
    </Transition>
    <!-- 空状态 -->
    <div v-if="!hasInputs" class="text-center text-surface-500 py-12">
      <div
        class="bg-transparent rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4"
      >
        <i class="pi pi-info-circle text-2xl text-surface-400"></i>
      </div>
      <p class="font-medium text-surface-600 mb-2">
        {{ $t('workflowApps.noInputsFound') }}
      </p>
      <p class="text-sm text-surface-500">
        {{ $t('workflowApps.clickPlusButtonToAddInputs') }}
      </p>
    </div>

    <!-- 输入列表 -->
    <div v-else class="space-y-2">
      <template v-for="(group, groupIndex) in groupedInputs" :key="groupIndex">
        <!-- 分组的图片输入 - 非编辑模式下使用分组显示 -->
        <WorkflowImageGroup
          v-if="
            group.type === 'group' && group.inputs.length > 0 && !isEditMode
          "
          :group="group"
          :input-values="inputValues"
          :file-metadata="fileMetadata"
          :upload-progress="uploadProgress"
          :is-edit-mode="isEditMode"
          :drag-over-input-id="dragOverInputId"
          :hovered-upload-input-id="hoveredUploadInputId"
          :hovered-preview-input-id="hoveredPreviewInputId"
          :get-image-url="getImageUrl"
          :get-file-url="getFileUrl"
          @file-upload="handleFileUpload"
          @open-file-selector="openFileSelector"
          @trigger-file-upload="triggerFileUpload"
          @clear-file-value="clearFileValue"
          @open-image-viewer="openImageViewer"
          @open-crop-existing-image="openCropExistingImage"
          @open-trim-existing-audio="openTrimExistingAudio"
          @open-trim-existing-video="openTrimExistingVideo"
          @paste-image="handlePasteImage"
          @handle-drag-over="handleDragOver"
          @handle-drag-leave="handleDragLeave"
          @handle-drop="handleDrop"
          @swap-images="handleSwapImages"
          @update:hovered-upload-input-id="hoveredUploadInputId = $event"
          @update:hovered-preview-input-id="hoveredPreviewInputId = $event"
        />
        <!-- 单个输入（非分组）或编辑模式下的所有输入 -->
        <template v-else>
          <WorkflowInputItem
            v-for="input in group.inputs"
            :key="input.id"
            :input="input"
            :input-value="inputValues[input.id]"
            :file-metadata="fileMetadata[input.id]"
            :upload-progress="uploadProgress[input.id] || 0"
            :is-edit-mode="isEditMode"
            :drag-over-input-id="dragOverInputId"
            :hovered-upload-input-id="hoveredUploadInputId"
            :hovered-preview-input-id="hoveredPreviewInputId"
            :image-url="getImageUrl(inputValues[input.id], input.id)"
            :file-url="getFileUrl(inputValues[input.id])"
            :input-type-options="inputTypeOptions"
            :optimized-input-ids="optimizedInputIds"
            :is-optimizing="isOptimizing"
            :current-optimizing-input-id="currentOptimizingInputId"
            :can-move-up="canMoveUp(input.id)"
            :can-move-down="canMoveDown(input.id)"
            :preset-uploading-for="presetUploadingFor"
            @remove-input="removeInput"
            @save-input-name="saveInputName"
            @edit-input-name="editInputName"
            @input-change="handleInputChange"
            @file-upload="handleFileUpload"
            @open-file-selector="openFileSelector"
            @trigger-file-upload="triggerFileUpload"
            @clear-file-value="clearFileValue"
            @open-image-viewer="openImageViewer"
            @open-crop-existing-image="openCropExistingImage"
            @open-trim-existing-audio="openTrimExistingAudio"
            @open-trim-existing-video="openTrimExistingVideo"
            @paste-image="handlePasteImage"
            @optimize-prompt="optimizePrompt"
            @revert-prompt="revertPrompt"
            @combo-click="handleComboClick"
            @update-input-config="updateInputConfig"
            @update-input-id="updateInputId"
            @move-input-up="moveInputUp"
            @move-input-down="moveInputDown"
            @add-combo-option="addComboOption"
            @update-combo-option="updateComboOption"
            @remove-combo-option="removeComboOption"
            @upload-audio-for-option="handleUploadAudioForOption"
            @upload-audio-for-default-value="handleUploadAudioForDefaultValue"
            @handle-drag-over="handleDragOver"
            @handle-drag-leave="handleDragLeave"
            @handle-drop="handleDrop"
            @update:hovered-upload-input-id="hoveredUploadInputId = $event"
            @update:hovered-preview-input-id="hoveredPreviewInputId = $event"
          />
        </template>
      </template>
    </div>

    <!-- 文件选择弹窗（图片/音频） -->
    <ImageSelectorDialog
      :visible="showImageSelector"
      :selected-image="selectedImage"
      :default-file-category="currentFileCategory"
      :app-id="props.shareId"
      :input-type="currentInputType"
      @update:visible="showImageSelector = $event"
      @select="handleImageSelect"
    />

    <!-- 图片编辑弹窗（裁剪/遮罩/画笔/叠加） -->
    <ImageEditor
      v-model="showCropper"
      :image-url="cropImageUrl"
      :title="$t('workflowApps.editImage')"
      :show-no-edit="true"
      :app-id="props.shareId"
      @confirm="handleCropConfirm"
      @no-edit="handleNoCrop"
      @cancel="handleCropCancel"
    />

    <!-- 音频裁剪弹窗 -->
    <AudioTrimmer
      v-model="showAudioTrimmer"
      :audio-url="trimAudioUrl"
      :max-duration="
        currentInputId
          ? props.workflowInputs.find((i) => i.id === currentInputId)?.config
              ?.maxDuration
          : undefined
      "
      :title="$t('workflowApps.trimAudio')"
      :cancel-label="$t('workflowApps.cancel')"
      :no-trim-label="$t('workflowApps.noTrim')"
      :confirm-label="$t('workflowApps.confirmTrim')"
      @confirm="handleTrimConfirm"
      @no-trim="handleNoTrim"
      @cancel="handleTrimCancel"
    />

    <!-- 视频裁剪弹窗 -->
    <VideoTrimmer
      v-model="showVideoTrimmer"
      :video-url="trimVideoUrl"
      :max-duration="
        currentInputId
          ? props.workflowInputs.find((i) => i.id === currentInputId)?.config
              ?.maxDuration
          : undefined
      "
      :title="$t('workflowApps.trimVideo')"
      :cancel-label="$t('workflowApps.cancel')"
      :no-trim-label="$t('workflowApps.noTrim')"
      :confirm-label="$t('workflowApps.confirmTrim')"
      @confirm="handleVideoTrimConfirm"
      @no-trim="handleVideoNoTrim"
      @cancel="handleVideoTrimCancel"
    />

    <!-- 遮罩编辑弹窗 -->
    <ImageMaskEditor
      v-model="showMaskEditor"
      :image-url="maskImageUrl"
      :title="$t('workflowApps.maskImage')"
      :cancel-label="$t('workflowApps.cancel')"
      :confirm-label="$t('workflowApps.confirmMask')"
      @confirm="handleMaskConfirm"
      @cancel="handleMaskCancel"
    />

    <!-- 图片查看器 -->
    <ImageViewerDialog
      v-model:visible="showImageViewer"
      :image-url="viewerImageUrl"
      :title="$t('workflowApps.imagePreview')"
    />

    <!-- 视频帧选择器 -->
    <VideoFrameSelector
      v-model:visible="showVideoFrameSelector"
      :video-url="selectedVideoUrl"
      :title="$t('workflowApps.selectVideoFrame')"
      @confirm="handleVideoFrameConfirm"
      @cancel="handleVideoFrameCancel"
    />
  </div>
</template>

<script setup lang="ts">
// import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import { useToast } from 'primevue/usetoast'
import { computed, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import AudioTrimmer from '@/components/common/AudioTrimmer.vue'
import ImageEditor from '@/components/common/ImageEditor.vue'
import ImageMaskEditor from '@/components/common/ImageMaskEditor.vue'
import ImageSelectorDialog from '@/components/common/ImageSelectorDialog.vue'
import ImageViewerDialog from '@/components/common/ImageViewerDialog.vue'
import VideoFrameSelector from '@/components/common/VideoFrameSelector.vue'
import VideoTrimmer from '@/components/common/VideoTrimmer.vue'
// import InputConfigEditor from '@/components/workflow/InputConfigEditor.vue'
import WorkflowImageGroup from '@/components/workflow/WorkflowImageGroup.vue'
import WorkflowInputItem from '@/components/workflow/WorkflowInputItem.vue'
import { FASTAPI_CONFIG } from '@/config/fastapi'
import { api } from '@/scripts/api'

interface WorkflowInput {
  id: string
  name: string
  type: string
  value: any
  description?: string
  placeholder?: string
  options?: any[]
  order?: number
  nodeId?: string
  widgetName?: string
  config: {
    min?: number
    max?: number
    step?: number
    defaultValue?: any
    required?: boolean
    order?: number
    llmPrompt?: string
    [key: string]: any
  }
}

interface WorkflowAppFormProps {
  workflowInputs?: WorkflowInput[]
  inputValues?: Record<string, any>
  isEditMode?: boolean
  isLoading?: boolean
  shareId?: number // 工作流应用分享ID（用于优化提示词时获取llmPrompt）
}

interface WorkflowAppFormEmits {
  (e: 'update:inputValues', value: Record<string, any>): void
  (e: 'input-change', inputId: string, value: any): void
  (e: 'remove-input', inputId: string): void
  (e: 'save-input-name', inputId: string, name?: string): void
  (e: 'save-input-value', inputId: string): void
  (e: 'save-input-description', inputId: string): void
  (e: 'save-input-config', inputId: string): void
  (e: 'save-input-options', inputId: string): void
  (e: 'save-input-type', inputId: string): void
  (
    e: 'update-input-id',
    oldInputId: string,
    newInputId: string,
    nodeId: string,
    widgetName: string
  ): void
  (e: 'add-option', inputId: string): void
  (e: 'remove-option', inputId: string, index: number): void
}

const props = withDefaults(defineProps<WorkflowAppFormProps>(), {
  workflowInputs: () => [],
  inputValues: () => ({}),
  isEditMode: false,
  isLoading: false
})

const emit = defineEmits<WorkflowAppFormEmits>()

const { t } = useI18n()
const toast = useToast()

// 存储文件的元数据（包括 obs_url 和原始文件名）
type FileMetadataItem = {
  name?: string
  obsUrl?: string
  originalFilename?: string
  fileId?: number
  previewUrl?: string
  isUploading?: boolean
}

const fileMetadata = ref<Record<string, FileMetadataItem>>({})

// 存储上传进度（inputId -> 进度百分比 0-100）
const uploadProgress = ref<Record<string, number>>({})

// 响应式数据
const showImageSelector = ref(false)
const selectedImage = ref('')
const currentInputId = ref('')
const currentFileCategory = ref<string>('image') // 当前文件选择器的类别：image/audio/video
const currentInputType = ref<string>('') // 当前输入类型，如 'file.image'、'file.audio' 或 'file.video'

// 裁剪相关状态
const showCropper = ref(false)
const cropImageUrl = ref('')
const originalFile = ref<File | null>(null)

// 遮罩编辑器相关状态
const showMaskEditor = ref(false)
const maskImageUrl = ref('')

// 音频裁剪相关状态
const showAudioTrimmer = ref(false)
const trimAudioUrl = ref('')
const originalAudioFile = ref<File | null>(null)

// 图片查看器相关状态
const showImageViewer = ref(false)
const viewerImageUrl = ref('')

// 视频帧选择器相关状态
const showVideoFrameSelector = ref(false)
const selectedVideoUrl = ref('')
const selectedVideoFile = ref<File | null>(null)

// 视频裁剪相关状态
const showVideoTrimmer = ref(false)
const trimVideoUrl = ref('')
const originalVideoFile = ref<File | null>(null)

// 优化提示词相关状态
const isOptimizing = ref(false)
const currentOptimizingInputId = ref<string>('')
// 存储每个输入框优化前的原始提示词
const originalPrompts = ref<Record<string, string>>({})
// 跟踪哪些输入框已经优化过
const optimizedInputIds = ref<Set<string>>(new Set())

// 拖拽上传相关状态
const dragOverInputId = ref<string>('')
const processingDropSet = new Set<string>() // 使用Set跟踪正在处理的输入ID，防止重复
const lastDropTime = new Map<string, number>() // 跟踪每个输入框最后一次处理的时间戳

// 文件上传悬停状态
const hoveredUploadInputId = ref<string | null>(null)
const hoveredPreviewInputId = ref<string | null>(null)

// 图片URL缓存（避免重复请求）
const imageUrlCache = new Map<string, string>() // key: `${inputId}_${filename}`, value: cached URL

// 已请求过 obs_url 的 filename 集合，避免重复请求
const obsUrlFetchAttempted = new Set<string>()

// 计算属性
const hasInputs = computed(() => props.workflowInputs.length > 0)

// 是否有文件正在上传中（未完成）
const hasUploadingFiles = computed(
  () =>
    Object.values(fileMetadata.value).some((m) => m?.isUploading) ||
    Object.keys(uploadProgress.value).length > 0
)

// 分组图片输入：根据名字模式（尾是1/2... 或 头是首/尾）分组
const groupedInputs = computed(() => {
  const imageInputs: WorkflowInput[] = []
  const otherInputs: WorkflowInput[] = []
  const imageGroups = new Map<string, WorkflowInput[]>()
  const firstLastGroupKeys = new Set<string>() // 首/尾 类型分组，不隐藏第二个

  // 模式1：任何名字后面跟数字或中文数字（参考图1、参考图2）
  const tailNumPattern = /^(.+?)([一二三四五六七八九十]+|\d+)$/
  // 模式2：名字以「首」或「尾」开头（首帧、尾帧）
  const firstLastPattern = /^(首|尾)(.+)$/

  props.workflowInputs.forEach((input) => {
    if (input.type === 'file.image') {
      // 优先检查首/尾模式
      const firstLastMatch = input.name.match(firstLastPattern)
      if (firstLastMatch) {
        const suffix = firstLastMatch[2].trim()
        if (suffix) {
          const groupKey = `首尾:${suffix}`
          firstLastGroupKeys.add(groupKey)
          if (!imageGroups.has(groupKey)) {
            imageGroups.set(groupKey, [])
          }
          imageGroups.get(groupKey)!.push(input)
        } else {
          imageInputs.push(input)
        }
        return
      }
      // 检查尾数字模式
      const match = input.name.match(tailNumPattern)
      if (match) {
        const baseName = match[1].trim()
        if (baseName) {
          if (!imageGroups.has(baseName)) {
            imageGroups.set(baseName, [])
          }
          imageGroups.get(baseName)!.push(input)
        } else {
          imageInputs.push(input)
        }
      } else {
        imageInputs.push(input)
      }
    } else {
      otherInputs.push(input)
    }
  })

  // 对每个分组内的输入按名字排序
  imageGroups.forEach((inputs, groupKey) => {
    inputs.sort((a, b) => {
      if (firstLastGroupKeys.has(groupKey)) {
        // 首/尾 分组：首在前，尾在后
        const aFirst = a.name.startsWith('首') ? 0 : 1
        const bFirst = b.name.startsWith('首') ? 0 : 1
        return aFirst - bFirst
      }
      // 尾数字分组：按数字排序
      const getNumber = (name: string): number => {
        const match = name.match(/(\d+|[一二三四五六七八九十]+)$/)
        if (!match) return 0
        const num = match[1]
        if (/^\d+$/.test(num)) return parseInt(num, 10)
        const cnNumbers: Record<string, number> = {
          一: 1,
          二: 2,
          三: 3,
          四: 4,
          五: 5,
          六: 6,
          七: 7,
          八: 8,
          九: 9,
          十: 10
        }
        return cnNumbers[num] || 0
      }
      return getNumber(a.name) - getNumber(b.name)
    })
  })

  // 构建结果：先显示分组的图片，然后显示单独的图片，最后显示其他输入
  const result: Array<{
    type: 'group' | 'single'
    inputs: WorkflowInput[]
    groupName?: string
    alwaysShowAll?: boolean
  }> = []

  // 添加分组的图片输入
  imageGroups.forEach((inputs, baseName) => {
    result.push({
      type: 'group',
      inputs,
      groupName: firstLastGroupKeys.has(baseName)
        ? baseName.replace(/^首尾:/, '')
        : baseName,
      alwaysShowAll: firstLastGroupKeys.has(baseName)
    })
  })

  // 添加单独的图片输入
  imageInputs.forEach((input) => {
    result.push({
      type: 'single',
      inputs: [input]
    })
  })

  // 添加其他输入
  otherInputs.forEach((input) => {
    result.push({
      type: 'single',
      inputs: [input]
    })
  })

  return result
})

// 输入类型选项
const inputTypeOptions = computed(() => [
  { label: t('workflowApps.inputTypes.text'), value: 'text' },
  { label: t('workflowApps.inputTypes.string'), value: 'string' },
  { label: t('workflowApps.inputTypes.number'), value: 'number' },
  { label: t('workflowApps.inputTypes.int'), value: 'int' },
  { label: t('workflowApps.inputTypes.float'), value: 'float' },
  { label: t('workflowApps.inputTypes.boolean'), value: 'boolean' },
  { label: t('workflowApps.inputTypes.combo'), value: 'combo' },
  { label: t('workflowApps.inputTypes.fileImage'), value: 'file.image' },
  { label: t('workflowApps.inputTypes.fileAudio'), value: 'file.audio' },
  {
    label: t('workflowApps.inputTypes.fileAudioSelect'),
    value: 'file.audio.select'
  },
  { label: t('workflowApps.inputTypes.fileVideo'), value: 'file.video' }
])

// 获取文件选择器的默认类别
const getFileSelectorCategory = (input: WorkflowInput): string => {
  if (input.type === 'file.image') return 'image'
  if (input.type === 'file.audio' || input.type === 'file.audio.select')
    return 'audio'
  if (input.type === 'file.video') return 'video'
  return 'image' // 默认
}

// 获取音频文件时长（秒）
const getAudioDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const audio = new Audio()
    const url = URL.createObjectURL(file)

    audio.addEventListener('loadedmetadata', () => {
      URL.revokeObjectURL(url)
      resolve(audio.duration)
    })

    audio.addEventListener('error', (error) => {
      URL.revokeObjectURL(url)
      reject(error)
    })

    audio.src = url
  })
}

// 获取视频文件时长（支持 File 或 URL）
const getVideoDuration = (fileOrUrl: File | string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.preload = 'metadata'

    let url = ''
    let shouldRevoke = false

    if (fileOrUrl instanceof File) {
      url = URL.createObjectURL(fileOrUrl)
      shouldRevoke = true
    } else {
      url = fileOrUrl
    }

    const cleanup = () => {
      if (shouldRevoke && url.startsWith('blob:')) {
        URL.revokeObjectURL(url)
      }
    }

    video.addEventListener('loadedmetadata', () => {
      cleanup()
      resolve(video.duration || 0)
    })

    video.addEventListener('error', (error) => {
      cleanup()
      reject(error)
    })

    video.src = url
  })
}

// 处理输入变更
const handleInputChange = (inputId: string, value: any) => {
  const input = props.workflowInputs.find((i) => i.id === inputId)

  // 文本类型最大字数校验
  const maxLength = input?.config?.maxLength
  if (
    input &&
    maxLength &&
    (input.type === 'text' ||
      input.type === 'string' ||
      input.type === 'customtext' ||
      input.type === 'multiline')
  ) {
    const valueStr = String(value ?? '')
    if (valueStr.length > maxLength) {
      toast.add({
        severity: 'warn',
        summary: t('g.warning'),
        detail: t('validation.maxLength', { length: maxLength }),
        life: 3000
      })
      return
    }
  }

  emit('input-change', inputId, value)

  // 检查该输入框是否支持优化提示词
  if (
    input &&
    (input.type === 'customtext' ||
      input.type === 'text' ||
      input.type === 'string' ||
      input.type === 'multiline') &&
    input.config?.llmPrompt === '1'
  ) {
    const currentValue = String(value || '').trim()
    const originalPrompt = originalPrompts.value[inputId]

    // 如果输入框被清空，清除优化状态
    if (!currentValue) {
      optimizedInputIds.value.delete(inputId)
      delete originalPrompts.value[inputId]
    }
    // 如果输入框的值与原始提示词不同（说明用户手动编辑了），也清除优化状态
    else if (originalPrompt && currentValue !== originalPrompt) {
      // 检查当前值是否与优化后的值相同（如果相同，说明是优化后的值，不应该清除）
      // 如果不同，说明用户手动编辑了，应该清除优化状态
      const currentOptimizedValue = props.inputValues[inputId]
      if (currentValue !== String(currentOptimizedValue || '').trim()) {
        optimizedInputIds.value.delete(inputId)
        delete originalPrompts.value[inputId]
      }
    }
  }
}

// 互换两个图片输入的值
const handleSwapImages = (inputId1: string, inputId2: string) => {
  const v1 = props.inputValues[inputId1]
  const v2 = props.inputValues[inputId2]
  emit('input-change', inputId1, v2)
  emit('input-change', inputId2, v1)
  // 同时互换 fileMetadata（预览、obsUrl 等）
  const m1 = fileMetadata.value[inputId1]
  const m2 = fileMetadata.value[inputId2]
  if (m1 || m2) {
    fileMetadata.value = {
      ...fileMetadata.value,
      [inputId1]: m2 ?? {},
      [inputId2]: m1 ?? {}
    }
  }
}

// 处理combo类型的点击事件
const handleComboClick = (_inputId: string, _event: Event) => {
  // 处理下拉选择点击事件
}

// 打开图片选择器
// 打开文件选择器（图片/音频）
const openFileSelector = (input: WorkflowInput) => {
  currentInputId.value = input.id
  selectedImage.value = props.inputValues[input.id] || ''
  currentFileCategory.value = getFileSelectorCategory(input)
  // file.audio.select 在素材库选择时按 file.audio 处理
  currentInputType.value =
    input.type === 'file.audio.select' ? 'file.audio' : input.type
  showImageSelector.value = true
}

// 从URL中提取文件名
const extractFilenameFromUrl = (url: string): string | null => {
  if (!url) return null

  try {
    // 如果是OBS链接，提取文件名
    if (url.includes('obs.cn-south-1.myhuaweicloud.com')) {
      const urlParts = url.split('/')
      const filename = urlParts[urlParts.length - 1].split('?')[0] // 去掉可能存在的query参数
      return filename || null
    }

    // 如果是其他HTTP链接，尝试从路径中提取文件名
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const urlObj = new URL(url)
      const pathname = urlObj.pathname
      const filename = pathname.split('/').pop() || null
      return filename
    }

    // 如果只是文件名，直接返回
    return url
  } catch (error) {
    console.error('提取文件名失败:', error)
    return null
  }
}

// 获取代理图片URL（用于裁剪，避免跨域）
const getProxyImageUrl = (imageFilename: string, inputId?: string): string => {
  if (!imageFilename) return ''

  const meta = inputId ? fileMetadata.value[inputId] : undefined

  // 优先使用 obs_url，并使用代理避免跨域
  if (meta?.obsUrl) {
    if (meta.obsUrl.includes('obs.cn-south-1.myhuaweicloud.com')) {
      // 提取文件名部分
      const urlParts = meta.obsUrl.split('/')
      const filename = urlParts[urlParts.length - 1].split('?')[0] // 去掉可能存在的query参数
      // 使用后端代理URL（移除timestamp避免重复请求）
      return `${FASTAPI_CONFIG.BASE_URL}/api/v1/upload/proxy/${filename}`
    }
    return meta.obsUrl
  }

  // 如果已经是完整的http(s)链接
  if (
    imageFilename.startsWith('http://') ||
    imageFilename.startsWith('https://')
  ) {
    // 如果是OBS链接，使用代理接口
    if (imageFilename.includes('obs.cn-south-1.myhuaweicloud.com')) {
      // 提取文件名部分
      const urlParts = imageFilename.split('/')
      const filename = urlParts[urlParts.length - 1].split('?')[0] // 去掉可能存在的query参数
      // 使用后端代理URL（移除timestamp避免重复请求）
      return `${FASTAPI_CONFIG.BASE_URL}/api/v1/upload/proxy/${filename}`
    }
    return imageFilename
  }

  // 如果只是文件名，使用后端代理URL
  const actualFilename = imageFilename.includes('/')
    ? imageFilename.split('/').pop() || imageFilename
    : imageFilename

  // 移除timestamp避免重复请求
  return `${FASTAPI_CONFIG.BASE_URL}/api/v1/upload/proxy/${actualFilename}`
}

// 获取代理视频URL（用于视频帧选择，避免跨域）
const getProxyVideoUrl = (videoFilename: string, videoUrl?: string): string => {
  // 优先使用提供的完整URL
  if (videoUrl) {
    // 如果是OBS链接，使用代理接口
    if (videoUrl.includes('obs.cn-south-1.myhuaweicloud.com')) {
      const urlParts = videoUrl.split('/')
      const filename = urlParts[urlParts.length - 1].split('?')[0]
      // 移除timestamp避免重复请求
      return `${FASTAPI_CONFIG.BASE_URL}/api/v1/upload/proxy/${filename}`
    }
    // 如果已经是完整的http(s)链接，直接返回
    if (videoUrl.startsWith('http://') || videoUrl.startsWith('https://')) {
      return videoUrl
    }
  }

  // 如果只是文件名，使用后端代理URL
  const actualFilename = videoFilename.includes('/')
    ? videoFilename.split('/').pop() || videoFilename
    : videoFilename

  // 移除timestamp避免重复请求
  return `${FASTAPI_CONFIG.BASE_URL}/api/v1/upload/proxy/${actualFilename}`
}

// 打开裁剪已选择的音频
const openTrimExistingAudio = (inputId: string) => {
  currentInputId.value = inputId
  const audioFilename = props.inputValues[inputId]

  if (!audioFilename) return

  // 清理之前的裁剪音频URL
  if (trimAudioUrl.value) {
    URL.revokeObjectURL(trimAudioUrl.value)
  }

  // 使用代理URL或OBS URL
  trimAudioUrl.value = getFileUrl(audioFilename)

  // 清空原始文件引用（因为是从服务器加载的音频）
  originalAudioFile.value = null

  // 打开裁剪弹窗
  showAudioTrimmer.value = true
}

// 打开裁剪已选择的视频
const openTrimExistingVideo = (inputId: string) => {
  currentInputId.value = inputId
  const videoFilename = props.inputValues[inputId]

  if (!videoFilename) return

  if (trimVideoUrl.value && trimVideoUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(trimVideoUrl.value)
  }

  trimVideoUrl.value = getFileUrl(videoFilename)
  originalVideoFile.value = null
  showVideoTrimmer.value = true
}

// 打开裁剪已选择的图片
const openCropExistingImage = (inputId: string) => {
  currentInputId.value = inputId
  const imageFilename = props.inputValues[inputId]

  if (!imageFilename) return

  // 清理之前的裁剪图片URL
  if (cropImageUrl.value) {
    URL.revokeObjectURL(cropImageUrl.value)
  }

  // 使用代理URL（避免跨域），传入 inputId 以便获取 obs_url
  cropImageUrl.value = getProxyImageUrl(imageFilename, inputId)

  // 清空原始文件引用（因为是从服务器加载的图片）
  originalFile.value = null

  // 打开裁剪弹窗
  showCropper.value = true
}

// 触发文件上传（图片/音频）
// 预设选项上传中状态 { inputId, optionIndex } 或 默认值上传 { inputId, type: 'default' }
const presetUploadingFor = ref<{
  inputId: string
  optionIndex?: number
  type?: 'default'
} | null>(null)

// 为预设选项上传音频（配置编辑时）
const handleUploadAudioForOption = async (
  inputId: string,
  optionIndex: number,
  file: File
) => {
  if (!file.type.startsWith('audio/')) {
    toast.add({
      severity: 'error',
      summary: t('workflowApps.uploadError'),
      detail: t('workflowApps.invalidAudioFile'),
      life: 3000
    })
    return
  }
  presetUploadingFor.value = { inputId, optionIndex }
  try {
    const filename = await uploadAudioForPresetOption(file, inputId)
    if (filename) {
      updateComboOption(inputId, optionIndex, 'value', filename)
      toast.add({
        severity: 'success',
        summary: t('g.success'),
        detail: t('workflowApps.uploadSuccess'),
        life: 2000
      })
    }
  } catch (error) {
    console.error('预设音频上传失败:', error)
    toast.add({
      severity: 'error',
      summary: t('workflowApps.uploadError'),
      detail: t('workflowApps.uploadAudioFailed'),
      life: 5000
    })
  } finally {
    presetUploadingFor.value = null
  }
}

// 为默认值上传音频（配置编辑时，选择自定义后上传）
const handleUploadAudioForDefaultValue = async (
  inputId: string,
  file: File
) => {
  if (!file.type.startsWith('audio/')) {
    toast.add({
      severity: 'error',
      summary: t('workflowApps.uploadError'),
      detail: t('workflowApps.invalidAudioFile'),
      life: 3000
    })
    return
  }
  presetUploadingFor.value = { inputId, type: 'default' }
  try {
    const filename = await uploadAudioForPresetOption(file, inputId)
    if (filename) {
      updateInputConfig(inputId, 'value', filename)
      toast.add({
        severity: 'success',
        summary: t('g.success'),
        detail: t('workflowApps.uploadSuccess'),
        life: 2000
      })
    }
  } catch (error) {
    console.error('默认值音频上传失败:', error)
    toast.add({
      severity: 'error',
      summary: t('workflowApps.uploadError'),
      detail: t('workflowApps.uploadAudioFailed'),
      life: 5000
    })
  } finally {
    presetUploadingFor.value = null
  }
}

// 上传音频用于预设选项，返回文件名
const uploadAudioForPresetOption = async (
  file: File,
  inputId: string
): Promise<string> => {
  const token = await api.getAuthToken()
  const input = props.workflowInputs.find((i) => i.id === inputId)
  const maxDuration = input?.config?.maxDuration

  const formData = new FormData()
  formData.append('audio', file)
  if (maxDuration) {
    formData.append('max_duration', String(maxDuration))
  }
  formData.append('subfolder', 'workflow-apps')

  return new Promise((resolve, reject) => {
    const url = `${FASTAPI_CONFIG.BASE_URL}/api/v1/comfyui/upload/audio`
    const xhr = new XMLHttpRequest()

    xhr.open('POST', url, true)
    xhr.setRequestHeader('Comfy-User', api.user || '')
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText)
          if (result.name) {
            const uploadedFilename = result.subfolder
              ? `${result.subfolder}/${result.name}`
              : result.name
            resolve(getBasename(uploadedFilename))
          } else {
            reject(new Error('Upload response missing name'))
          }
        } catch (error) {
          reject(error)
        }
      } else {
        try {
          const err = JSON.parse(xhr.responseText)
          reject(new Error(err.detail || `Upload failed: ${xhr.status}`))
        } catch {
          reject(new Error(`Upload failed: ${xhr.status}`))
        }
      }
    })

    xhr.addEventListener('error', () =>
      reject(new Error('Upload request failed'))
    )
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')))

    xhr.send(formData)
  })
}

const triggerFileUpload = (input: WorkflowInput) => {
  const fileInput = document.querySelector(
    `input[type="file"][data-input-id="${input.id}"]`
  ) as HTMLInputElement
  if (fileInput) {
    fileInput.click()
  }
}

// 处理拖拽悬停
const handleDragOver = (event: DragEvent, inputId: string) => {
  event.preventDefault()
  event.stopPropagation()
  dragOverInputId.value = inputId
}

// 处理拖拽离开
const handleDragLeave = (event: DragEvent) => {
  event.preventDefault()
  event.stopPropagation()
  // 只有当真正离开拖拽区域时才清除状态
  const target = event.target as HTMLElement
  const relatedTarget = event.relatedTarget as HTMLElement
  if (
    !target.closest('.drag-drop-zone') ||
    (relatedTarget && !relatedTarget.closest('.drag-drop-zone'))
  ) {
    dragOverInputId.value = ''
  }
}

// 处理拖拽放置
const handleDrop = async (event: DragEvent, input: WorkflowInput) => {
  // 使用事件对象本身来标记，防止同一个事件被处理两次
  const eventKey = `__handled_${input.id}`
  if ((event as any)[eventKey]) {
    console.log('事件已被处理过，跳过:', input.id)
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    return
  }
  // 立即标记事件
  ;(event as any)[eventKey] = true

  const now = Date.now()
  const lastTime = lastDropTime.get(input.id) || 0

  // 防止重复处理 - 检查是否正在处理或最近刚处理过（1000ms内）
  if (processingDropSet.has(input.id) || now - lastTime < 1000) {
    console.log('拖拽处理已在进行中或刚处理过，跳过:', input.id, {
      inSet: processingDropSet.has(input.id),
      timeSinceLast: now - lastTime
    })
    // 即使跳过，也要阻止事件传播
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    return
  }

  // 立即添加到Set，防止重复处理
  processingDropSet.add(input.id)
  lastDropTime.set(input.id, now)

  // 立即阻止事件传播，防止重复触发（包括全局监听器）
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()

  dragOverInputId.value = ''

  // 检查事件目标，确保只处理我们自己的元素
  const target = event.target as HTMLElement
  const currentTarget = event.currentTarget as HTMLElement
  // 如果目标不是当前元素或其子元素，跳过
  if (target && currentTarget && !currentTarget.contains(target)) {
    console.log('事件目标不匹配，跳过:', input.id)
    processingDropSet.delete(input.id)
    return
  }

  console.log('开始处理拖拽:', input.id, {
    target: target?.className,
    currentTarget: currentTarget?.className,
    targetTag: target?.tagName,
    currentTargetTag: currentTarget?.tagName
  })

  try {
    // 优先检查是否有URL数据（从历史记录拖拽的图片）
    const imageUrl =
      event.dataTransfer?.getData('application/x-image-url') ||
      event.dataTransfer?.getData('text/plain')

    // 如果检测到URL（从历史记录拖拽），优先处理URL，忽略文件
    if (
      imageUrl &&
      (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))
    ) {
      // 从历史记录拖拽的图片，直接使用URL
      if (input.type === 'file.image') {
        // 直接处理，不需要确认覆盖
        processDroppedImageUrl(imageUrl, input)
        setTimeout(() => {
          processingDropSet.delete(input.id)
          console.log('拖拽处理完成:', input.id)
        }, 1000)
        return
      }
    }

    // 如果没有URL，处理文件拖拽（从文件系统拖拽）
    const files = event.dataTransfer?.files
    if (!files || files.length === 0) {
      processingDropSet.delete(input.id)
      console.log('拖拽处理完成（无文件）:', input.id)
      return
    }

    // 只处理第一个文件
    const file = files[0]

    // 验证文件类型
    if (input.type === 'file.image') {
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        toast.add({
          severity: 'error',
          summary: t('workflowApps.uploadError'),
          detail: t('workflowApps.invalidImageFile'),
          life: 3000
        })
        setTimeout(() => {
          processingDropSet.delete(input.id)
          console.log('拖拽处理完成（无效文件类型）:', input.id)
        }, 1000)
        return
      }
    } else if (
      input.type === 'file.audio' ||
      input.type === 'file.audio.select'
    ) {
      if (!file.type.startsWith('audio/')) {
        toast.add({
          severity: 'error',
          summary: t('workflowApps.uploadError'),
          detail: t('workflowApps.invalidAudioFile'),
          life: 3000
        })
        setTimeout(() => {
          processingDropSet.delete(input.id)
          console.log('拖拽处理完成（无效音频类型）:', input.id)
        }, 1000)
        return
      }
    } else if (input.type === 'file.video') {
      if (!isVideoFile(file)) {
        toast.add({
          severity: 'error',
          summary: t('workflowApps.uploadError'),
          detail: t('workflowApps.invalidVideoFile'),
          life: 3000
        })
        setTimeout(() => {
          processingDropSet.delete(input.id)
          console.log('拖拽处理完成（无效视频类型）:', input.id)
        }, 1000)
        return
      }
    }

    // 直接处理，不需要确认覆盖
    await processDroppedFile(file, input)
    setTimeout(() => {
      processingDropSet.delete(input.id)
      console.log('拖拽处理完成:', input.id)
    }, 1000)
  } catch (error) {
    console.error('处理拖拽失败:', error, input.id)
    setTimeout(() => {
      processingDropSet.delete(input.id)
    }, 1000)
  }
}

// 处理从历史记录拖拽的图片URL
const processDroppedImageUrl = (imageUrl: string, input: WorkflowInput) => {
  if (input.type === 'file.image') {
    // 检查是否是视频文件
    if (isVideoFileFromUrl(imageUrl)) {
      // 视频文件，打开视频帧选择器
      currentInputId.value = input.id

      // 清理之前的视频URL
      if (
        selectedVideoUrl.value &&
        selectedVideoUrl.value.startsWith('blob:')
      ) {
        URL.revokeObjectURL(selectedVideoUrl.value)
      }

      // 设置视频URL（VideoFrameSelector会自己下载）
      selectedVideoUrl.value = imageUrl
      selectedVideoFile.value = null

      // 打开视频帧选择弹窗
      showVideoFrameSelector.value = true
    } else {
      // 普通图片，直接使用URL
      // 从URL中提取文件名
      const filename = extractFilenameFromUrl(imageUrl)
      if (filename) {
        // 直接设置文件名，不需要重新上传
        emit('input-change', input.id, filename)

        // 设置文件元数据，包含obsUrl用于预览
        setFileMetadata(input.id, {
          obsUrl: imageUrl,
          originalFilename: filename,
          isUploading: false
        })
      }
    }
  }
}

// 处理拖拽的文件
const processDroppedFile = async (file: File, input: WorkflowInput) => {
  // 如果是图片输入类型，检查是否为视频文件
  if (input.type === 'file.image' && isVideoFile(file)) {
    // 保存当前输入ID
    currentInputId.value = input.id

    // 保存视频文件
    selectedVideoFile.value = file

    // 清理之前的视频URL
    if (selectedVideoUrl.value && selectedVideoUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(selectedVideoUrl.value)
    }

    // 创建临时URL用于视频帧选择
    selectedVideoUrl.value = URL.createObjectURL(file)

    // 打开视频帧选择弹窗
    showVideoFrameSelector.value = true
  } else if (input.type === 'file.image') {
    // 图片默认不裁剪，直接上传原图
    currentInputId.value = input.id
    try {
      await uploadFile(file, input.id, 'workflow-apps')
    } catch (error) {
      console.error('上传图片失败:', error)
    }
  } else if (
    input.type === 'file.audio' ||
    input.type === 'file.audio.select'
  ) {
    // 音频默认不裁剪，直接上传原音频
    await processAudioFile(file, input)
  } else if (input.type === 'file.video') {
    // 视频默认不裁剪，直接上传原视频
    await processVideoFile(file, input)
  }
}

// 处理音频文件（直接上传，不裁剪）
const processAudioFile = async (file: File, input: WorkflowInput) => {
  // 音频默认不裁剪，直接上传原音频
  currentInputId.value = input.id

  // 音频最大时长校验
  const maxDuration = input?.config?.maxDuration
  if (maxDuration) {
    try {
      const duration = await getAudioDuration(file)
      if (duration > maxDuration) {
        toast.add({
          severity: 'warn',
          summary: t('g.warning'),
          detail: t('validation.maxDuration', { duration: maxDuration }),
          life: 5000
        })
        return
      }
    } catch (error) {
      console.warn('获取音频时长失败:', error)
      // 如果获取时长失败，仍然允许上传，由后端验证
    }
  }

  try {
    await uploadFile(file, input.id)
  } catch (error) {
    console.error('上传音频失败:', error)
    // 如果是后端返回的时长超限错误，优先给出更友好的提示
    if (
      error instanceof Error &&
      (error.message.includes('exceeds maximum allowed duration') ||
        error.message.includes('maxDuration') ||
        error.message.includes('duration'))
    ) {
      const maxDuration = input?.config?.maxDuration
      toast.add({
        severity: 'warn',
        summary: t('g.warning'),
        detail: t('validation.maxDuration', { duration: maxDuration || 0 }),
        life: 5000
      })
    } else {
      // 其他错误使用通用上传失败提示
      toast.add({
        severity: 'error',
        summary: t('workflowApps.uploadError'),
        detail: t('workflowApps.uploadAudioFailed'),
        life: 5000
      })
    }
  }
}

// 处理视频文件（直接上传，不裁剪）
const processVideoFile = async (file: File, input: WorkflowInput) => {
  // 视频默认不裁剪，直接上传原视频
  currentInputId.value = input.id

  // 视频最大时长校验
  const maxDuration = input?.config?.maxDuration
  if (maxDuration) {
    try {
      const duration = await getVideoDuration(file)
      if (duration > maxDuration) {
        toast.add({
          severity: 'warn',
          summary: t('g.warning'),
          detail: t('validation.maxDuration', { duration: maxDuration }),
          life: 5000
        })
        return
      }
    } catch (error) {
      console.warn('获取视频时长失败:', error)
      // 如果获取时长失败，仍然允许上传，由后端验证
    }
  }

  try {
    await uploadFile(file, input.id)
  } catch (error) {
    console.error('上传视频失败:', error)
    toast.add({
      severity: 'error',
      summary: t('workflowApps.uploadError'),
      detail: t('workflowApps.uploadVideoFailed'),
      life: 5000
    })
  }
}

// 处理图片上传
// 判断URL是否是视频文件
const isVideoFileFromUrl = (url: string): boolean => {
  if (!url) return false
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.gif']
  const lowerUrl = url.toLowerCase()
  return videoExtensions.some((ext) => lowerUrl.includes(ext))
}

// 判断是否为视频文件
const isVideoFile = (file: File): boolean => {
  // 首先检查 MIME 类型
  if (file.type && file.type.startsWith('video/')) {
    return true
  }
  // 如果 MIME 类型为空，通过文件扩展名判断
  const fileName = file.name.toLowerCase()
  const videoExtensions = [
    '.mp4',
    '.webm',
    '.mov',
    '.avi',
    '.mkv',
    '.flv',
    '.wmv',
    '.m4v'
  ]
  return videoExtensions.some((ext) => fileName.endsWith(ext))
}

// 处理文件上传（图片/音频/视频）
const handleFileUpload = async (event: Event, input: WorkflowInput) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) {
    // 如果是图片输入类型，检查是否为视频文件
    if (input.type === 'file.image' && isVideoFile(file)) {
      // 保存当前输入ID
      currentInputId.value = input.id

      // 保存视频文件
      selectedVideoFile.value = file

      // 清理之前的视频URL
      if (
        selectedVideoUrl.value &&
        selectedVideoUrl.value.startsWith('blob:')
      ) {
        URL.revokeObjectURL(selectedVideoUrl.value)
      }

      // 创建临时URL用于视频帧选择
      selectedVideoUrl.value = URL.createObjectURL(file)

      // 打开视频帧选择弹窗
      showVideoFrameSelector.value = true
    } else if (input.type === 'file.image') {
      // 图片默认不裁剪，直接上传原图
      currentInputId.value = input.id
      try {
        await uploadFile(file, input.id, 'workflow-apps')
      } catch (error) {
        console.error('上传图片失败:', error)
      }
    } else if (
      input.type === 'file.audio' ||
      input.type === 'file.audio.select'
    ) {
      // 音频默认不裁剪，直接上传原音频
      await processAudioFile(file, input)
    } else if (input.type === 'file.video') {
      if (!isVideoFile(file)) {
        toast.add({
          severity: 'error',
          summary: t('workflowApps.uploadError'),
          detail: t('workflowApps.invalidVideoFile'),
          life: 3000
        })
        return
      }
      await processVideoFile(file, input)
    }
  }

  // 重置文件输入，允许重复选择同一文件
  target.value = ''
}

// 处理粘贴的图片
const handlePasteImage = async (file: File, input: WorkflowInput) => {
  if (!file) {
    return
  }

  // 只处理图片类型的输入
  if (input.type !== 'file.image') {
    return
  }

  // 检查是否为视频文件（图片输入也支持视频）
  if (isVideoFile(file)) {
    // 保存当前输入ID
    currentInputId.value = input.id

    // 保存视频文件
    selectedVideoFile.value = file

    // 清理之前的视频URL
    if (selectedVideoUrl.value && selectedVideoUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(selectedVideoUrl.value)
    }

    // 创建临时URL用于视频帧选择
    selectedVideoUrl.value = URL.createObjectURL(file)

    // 打开视频帧选择弹窗
    showVideoFrameSelector.value = true
  } else {
    // 图片默认不裁剪，直接上传原图
    currentInputId.value = input.id
    try {
      await uploadFile(file, input.id, 'workflow-apps')
    } catch (error) {
      console.error('上传图片失败:', error)
    }
  }
}

// 上传文件到服务器（带进度跟踪）
const uploadFile = async (file: File, inputId: string, subfolder?: string) => {
  // 获取认证token
  const token = await api.getAuthToken()

  const existingMeta = fileMetadata.value[inputId]
  let previewUrl: string | undefined
  const isImageFile = file.type?.startsWith('image/')
  const isAudioFile = file.type?.startsWith('audio/')
  const isVideoUpload = isVideoFile(file)

  if (isImageFile || isVideoUpload) {
    if (existingMeta?.previewUrl) {
      cleanupPreviewUrl(existingMeta.previewUrl)
    }
    previewUrl = URL.createObjectURL(file)
  }

  const metadataUpdate: Partial<FileMetadataItem> = {
    originalFilename: file.name,
    isUploading: true
  }

  if (previewUrl) {
    metadataUpdate.previewUrl = previewUrl
  }

  mergeFileMetadata(inputId, metadataUpdate)

  return new Promise<void>((resolve, reject) => {
    // 初始化进度
    uploadProgress.value[inputId] = 0

    const formData = new FormData()

    // 根据文件类型选择不同的端点和字段名
    let url: string
    if (isAudioFile) {
      // 音频文件使用专门的音频上传端点
      formData.append('audio', file)
      url = `${FASTAPI_CONFIG.BASE_URL}/api/v1/comfyui/upload/audio`

      // 获取输入配置中的 maxDuration
      const input = props.workflowInputs.find((i) => i.id === inputId)
      const maxDuration = input?.config?.maxDuration
      if (maxDuration) {
        formData.append('max_duration', String(maxDuration))
      }
    } else if (isVideoUpload) {
      formData.append('video', file)
      url = `${FASTAPI_CONFIG.BASE_URL}/api/v1/comfyui/upload/video`

      const input = props.workflowInputs.find((i) => i.id === inputId)
      const maxDuration = input?.config?.maxDuration
      if (maxDuration) {
        formData.append('max_duration', String(maxDuration))
      }
    } else {
      // 图片和其他文件使用图片上传端点（画布直接上传时 folder_name 填「上传」）
      formData.append('image', file) // 保持字段名为 'image' 以兼容现有 API
      url = `${FASTAPI_CONFIG.BASE_URL}/api/v1/upload/image`
      formData.append('folder_name', '上传')
    }

    if (subfolder) {
      formData.append('subfolder', subfolder)
    }

    const xhr = new XMLHttpRequest()

    // 设置请求头
    xhr.open('POST', url, true)
    xhr.setRequestHeader('Comfy-User', api.user || '')
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    }

    // 监听上传进度
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100)
        uploadProgress.value[inputId] = percentComplete
      }
    })

    // 监听完成事件
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText)
          if (result.name) {
            // 处理文件名（如果有 subfolder，需要包含在文件名中）
            const uploadedFilename = result.subfolder
              ? `${result.subfolder}/${result.name}`
              : result.name

            // 保存纯文件名到 inputValues（用于提交给 ComfyUI）
            const cleanFilename = getBasename(uploadedFilename)
            emit('input-change', inputId, cleanFilename)

            const currentMeta = fileMetadata.value[inputId] || {}
            const newMeta: FileMetadataItem = {
              ...currentMeta,
              name: result.name,
              obsUrl: result.obs_url || currentMeta.obsUrl,
              originalFilename:
                result.original_filename ||
                currentMeta.originalFilename ||
                file.name,
              fileId: result.file_id,
              isUploading: false
            }

            if (previewUrl) {
              cleanupPreviewUrl(previewUrl)
              delete newMeta.previewUrl
            }

            setFileMetadata(inputId, newMeta)
          } else {
            mergeFileMetadata(inputId, { isUploading: false })
          }
          // 清除进度
          delete uploadProgress.value[inputId]
          resolve()
        } catch (error) {
          console.error('解析响应失败:', error)
          delete uploadProgress.value[inputId]
          mergeFileMetadata(inputId, { isUploading: false })
          reject(error)
        }
      } else {
        console.error('上传失败:', xhr.status, xhr.statusText)
        delete uploadProgress.value[inputId]
        mergeFileMetadata(inputId, { isUploading: false })

        // 尝试解析错误信息
        let errorMessage = `Upload failed: ${xhr.status} - ${xhr.statusText}`
        try {
          const errorResult = JSON.parse(xhr.responseText)
          if (errorResult.detail) {
            errorMessage = errorResult.detail
            // 如果是时长超限错误，显示友好的提示
            if (xhr.status === 400 && errorResult.detail.includes('duration')) {
              const input = props.workflowInputs.find((i) => i.id === inputId)
              toast.add({
                severity: 'error',
                summary: t('g.error'),
                detail: t('validation.maxDuration', {
                  duration: input?.config?.maxDuration || 0
                }),
                life: 5000
              })
            }
          }
        } catch (e) {
          // 忽略解析错误
        }

        reject(new Error(errorMessage))
      }
    })

    // 监听错误事件
    xhr.addEventListener('error', () => {
      console.error('上传请求失败')
      delete uploadProgress.value[inputId]
      mergeFileMetadata(inputId, { isUploading: false })
      reject(new Error('Upload request failed'))
    })

    // 监听中止事件
    xhr.addEventListener('abort', () => {
      console.warn('上传被中止')
      delete uploadProgress.value[inputId]
      mergeFileMetadata(inputId, { isUploading: false })
      reject(new Error('Upload aborted'))
    })

    // 发送请求
    xhr.send(formData)
  })
}

// 清除文件值
const clearFileValue = (inputId: string) => {
  emit('input-change', inputId, '')
  const metadata = fileMetadata.value[inputId]
  if (metadata?.previewUrl) {
    cleanupPreviewUrl(metadata.previewUrl)
  }

  if (fileMetadata.value[inputId]) {
    const { [inputId]: _removed, ...rest } = fileMetadata.value
    fileMetadata.value = rest
  }

  if (uploadProgress.value[inputId] !== undefined) {
    const { [inputId]: _progress, ...rest } = uploadProgress.value
    uploadProgress.value = rest
  }

  // 清除该inputId相关的URL缓存
  const keysToDelete: string[] = []
  imageUrlCache.forEach((_value, key) => {
    if (key.startsWith(`${inputId}_`)) {
      keysToDelete.push(key)
    }
  })
  keysToDelete.forEach((key) => imageUrlCache.delete(key))
}

// 获取文件URL（用于音频等文件显示）
const getFileUrl = (filename: string) => {
  // 确保 filename 是字符串类型
  const filenameStr = filename != null ? String(filename) : ''
  if (!filenameStr) return ''

  // 如果是完整URL，直接返回
  if (filenameStr.startsWith('http://') || filenameStr.startsWith('https://')) {
    return filenameStr
  }

  // 优先从文件元数据中获取 OBS URL
  const inputId = Object.keys(props.inputValues).find(
    (key) => props.inputValues[key] === filename
  )

  if (inputId && fileMetadata.value[inputId]?.obsUrl) {
    return fileMetadata.value[inputId].obsUrl
  }

  // 使用后端 /view 端点（由后端解析文件位置，支持多 OBS 桶等）
  return `${FASTAPI_CONFIG.BASE_URL}/api/v1/comfyui/view?filename=${encodeURIComponent(filenameStr)}&type=input&subfolder=`
}

// 获取图片URL（用于显示）
const getImageUrl = (filename: string, inputId?: string) => {
  // 确保 filename 是字符串类型
  const filenameStr = filename != null ? String(filename) : ''

  const meta = inputId ? fileMetadata.value[inputId] : undefined

  // 如果正在上传，使用预览URL（不需要缓存）
  if (meta?.isUploading && meta.previewUrl) {
    return addImageProcessParam(meta.previewUrl)
  }

  // 如果有预览URL，直接返回（不需要缓存）
  if (meta?.previewUrl) {
    return addImageProcessParam(meta.previewUrl)
  }

  // 构建缓存key
  const cacheKey = inputId ? `${inputId}_${filenameStr}` : filenameStr

  // 检查缓存
  if (imageUrlCache.has(cacheKey)) {
    return addImageProcessParam(imageUrlCache.get(cacheKey)!)
  }

  let url = ''

  // 优先使用 obs_url（预览直接使用obs_url，不经过代理）
  if (meta?.obsUrl) {
    url = meta.obsUrl
  } else if (filenameStr) {
    // 如果已经是完整URL，直接使用
    if (
      filenameStr.startsWith('http://') ||
      filenameStr.startsWith('https://')
    ) {
      url = filenameStr
    } else {
      // 使用后端 /view 端点（由后端解析文件位置，支持多 OBS 桶等）
      const actualFilename = filenameStr.includes('/')
        ? filenameStr.split('/').pop() || filenameStr
        : filenameStr
      url = `${FASTAPI_CONFIG.BASE_URL}/api/v1/comfyui/view?filename=${encodeURIComponent(actualFilename)}&type=input&subfolder=`
    }
  }

  // 缓存URL（只有当URL不为空时才缓存）
  if (url) {
    imageUrlCache.set(cacheKey, url)
  }

  // 添加图片处理参数
  return addImageProcessParam(url)
}

// 添加图片处理参数
const addImageProcessParam = (url: string): string => {
  if (!url) return ''

  // 如果URL已经包含查询参数，使用&连接，否则使用?
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}x-image-process=image/resize,w_200`
}

// 获取原始图片URL（不使用代理，用于传给后端）
const getOriginalImageUrl = (filename: string, inputId?: string) => {
  const meta = inputId ? fileMetadata.value[inputId] : undefined

  // 优先使用 obs_url（原始URL，不使用代理）
  if (meta?.obsUrl) {
    return meta.obsUrl
  }

  if (filename) {
    // 如果已经是完整URL，直接返回（原始URL，不使用代理）
    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      return filename
    }

    // 使用后端 /view 端点（由后端解析文件位置，支持多 OBS 桶等）
    const actualFilename = filename.includes('/')
      ? filename.split('/').pop() || filename
      : filename
    return `${FASTAPI_CONFIG.BASE_URL}/api/v1/comfyui/view?filename=${encodeURIComponent(actualFilename)}&type=input&subfolder=`
  }

  return ''
}

// 打开图片查看器
const openImageViewer = (inputIdOrFilename: string) => {
  let filename = inputIdOrFilename
  let inputId: string | undefined

  if (
    Object.prototype.hasOwnProperty.call(props.inputValues, inputIdOrFilename)
  ) {
    inputId = inputIdOrFilename
    filename = props.inputValues[inputIdOrFilename]
  }

  // 使用原图URL，去掉缩略图后缀
  viewerImageUrl.value = getOriginalImageUrl(filename, inputId)
  showImageViewer.value = true
}

// 判断文件是否为视频（通过文件名或文件类别）
const isVideoFileFromSelector = (
  filename: string,
  fileCategory?: string
): boolean => {
  // 首先检查文件类别
  if (fileCategory === 'video') {
    return true
  }
  // 通过文件扩展名判断
  const fileName = filename.toLowerCase()
  const videoExtensions = [
    '.mp4',
    '.webm',
    '.mov',
    '.avi',
    '.mkv',
    '.flv',
    '.wmv',
    '.m4v'
  ]
  return videoExtensions.some((ext) => fileName.endsWith(ext))
}

// 处理图片选择
const handleImageSelect = async (imageUrl: string, imageObj?: any) => {
  if (!currentInputId.value) {
    showImageSelector.value = false
    return
  }

  const filename = imageObj?.filename || imageUrl
  const fileCategory = imageObj?.file_category
  const selectedInputType = currentInputType.value

  // 如果是视频输入，直接使用视频文件
  if (selectedInputType === 'file.video') {
    if (!isVideoFileFromSelector(filename, fileCategory)) {
      toast.add({
        severity: 'error',
        summary: t('workflowApps.uploadError'),
        detail: t('workflowApps.invalidVideoFile'),
        life: 3000
      })
      return
    }

    const videoUrl = imageObj?.obs_url || imageObj?.url || imageUrl
    const proxiedUrl = getProxyVideoUrl(filename, videoUrl)
    const input = props.workflowInputs.find(
      (i) => i.id === currentInputId.value
    )
    const maxDuration = input?.config?.maxDuration

    if (maxDuration) {
      try {
        const duration = await getVideoDuration(proxiedUrl)
        if (duration > maxDuration) {
          toast.add({
            severity: 'warn',
            summary: t('g.warning'),
            detail: t('validation.maxDuration', { duration: maxDuration }),
            life: 5000
          })
          return
        }
      } catch (error) {
        console.warn('获取视频时长失败:', error)
      }
    }

    emit('input-change', currentInputId.value, getBasename(filename))
    setFileMetadata(currentInputId.value, {
      name: filename,
      obsUrl: imageObj?.obs_url || proxiedUrl,
      originalFilename: imageObj?.original_filename || filename,
      fileId: imageObj?.id,
      isUploading: false
    })
    showImageSelector.value = false
    return
  }

  // 检查是否为视频文件（图片输入下选择视频用于截帧）
  if (isVideoFileFromSelector(filename, fileCategory)) {
    // 如果是视频，打开视频帧选择器
    // 获取视频的代理URL（避免跨域问题）
    const videoUrl = imageObj?.obs_url || imageObj?.url || imageUrl
    selectedVideoUrl.value = getProxyVideoUrl(filename, videoUrl)

    // 清空视频文件引用（因为是从服务器选择的，不是本地文件）
    selectedVideoFile.value = null

    // 关闭图片选择器
    showImageSelector.value = false

    // 打开视频帧选择器
    showVideoFrameSelector.value = true
    return
  }

  // 如果是图片，正常处理
  // 保存纯文件名到 inputValues（优先使用对象的 filename）
  const valueToSave = imageObj?.filename
    ? getBasename(imageObj.filename)
    : getBasename(imageUrl)
  emit('input-change', currentInputId.value, valueToSave)

  // 如果有完整的图片对象，保存文件元数据（用于显示原始文件名）
  if (imageObj?.obs_url) {
    const previousMeta = fileMetadata.value[currentInputId.value]
    if (previousMeta?.previewUrl) {
      cleanupPreviewUrl(previousMeta.previewUrl)
    }

    // 使用新对象确保 Vue 响应式更新
    setFileMetadata(currentInputId.value, {
      name: imageObj.filename,
      obsUrl: imageObj.obs_url,
      originalFilename: imageObj.original_filename || imageObj.filename,
      fileId: imageObj.id,
      isUploading: false
    })
  }
  showImageSelector.value = false
}

// 处理编辑确认
const handleCropConfirm = async (file: File) => {
  if (!currentInputId.value) return

  try {
    // 使用带进度的上传函数
    await uploadFile(file, currentInputId.value, 'workflow-apps')

    // 关闭裁剪弹窗
    showCropper.value = false
  } catch (error) {
    console.error('上传裁剪后的图片失败:', error)
  } finally {
    // 清理临时URL和原始文件引用
    if (cropImageUrl.value && cropImageUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(cropImageUrl.value)
    }
    cropImageUrl.value = ''
    originalFile.value = null
  }
}

// 不裁剪 - 上传原图
const handleNoCrop = async () => {
  // 如果没有原始文件（从服务器加载的图片），直接关闭弹窗
  if (!originalFile.value) {
    if (cropImageUrl.value && cropImageUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(cropImageUrl.value)
    }
    cropImageUrl.value = ''
    showCropper.value = false
    return
  }

  // 如果有原始文件，上传原图
  if (!currentInputId.value) {
    showCropper.value = false
    return
  }

  try {
    // 使用带进度的上传函数
    await uploadFile(originalFile.value, currentInputId.value, 'workflow-apps')

    // 关闭裁剪弹窗
    showCropper.value = false
  } catch (error) {
    console.error('上传原图失败:', error)
  } finally {
    // 清理临时URL和原始文件引用
    if (cropImageUrl.value && cropImageUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(cropImageUrl.value)
    }
    cropImageUrl.value = ''
    originalFile.value = null
  }
}

// 取消裁剪 - 关闭弹窗，不上传
const handleCropCancel = () => {
  // 清理临时URL和原始文件引用
  if (cropImageUrl.value && cropImageUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(cropImageUrl.value)
  }
  cropImageUrl.value = ''
  originalFile.value = null
  showCropper.value = false
}

// 遮罩编辑确认
const handleMaskConfirm = async (maskBlob: Blob) => {
  if (!currentInputId.value) return

  try {
    // 创建File对象
    const filename = `mask-${Date.now()}.png`
    const file = new File([maskBlob], filename, { type: 'image/png' })

    // 使用带进度的上传函数
    await uploadFile(file, currentInputId.value, 'workflow-apps/masks')

    // 关闭遮罩编辑弹窗
    showMaskEditor.value = false

    toast.add({
      severity: 'success',
      summary: t('workflowApps.maskSuccess'),
      detail: t('workflowApps.maskCreated'),
      life: 3000
    })
  } catch (error) {
    console.error('上传遮罩图片失败:', error)
    toast.add({
      severity: 'error',
      summary: t('workflowApps.uploadError'),
      detail: String(error),
      life: 3000
    })
  } finally {
    // 清理临时URL
    if (maskImageUrl.value && maskImageUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(maskImageUrl.value)
    }
    maskImageUrl.value = ''
  }
}

// 遮罩编辑取消
const handleMaskCancel = () => {
  // 清理临时URL
  if (maskImageUrl.value && maskImageUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(maskImageUrl.value)
  }
  maskImageUrl.value = ''
  showMaskEditor.value = false
}

// 编辑模式相关方法
const removeInput = (inputId: string) => {
  emit('remove-input', inputId)
}

const editInputName = (_inputId: string) => {
  // 切换到编辑模式
}

const saveInputName = (inputId: string, name?: string) => {
  console.log('[WorkflowAppForm] saveInputName 接收参数:', { inputId, name })
  emit('save-input-name', inputId, name)
}

// 这些方法保留用于兼容性，但实际使用 updateInputConfig
// @ts-expect-error - Reserved for future use

const saveInputValue = (inputId: string) => {
  emit('save-input-value', inputId)
}

// @ts-expect-error - Reserved for future use

const saveInputDescription = (inputId: string) => {
  emit('save-input-description', inputId)
}

// @ts-expect-error - Reserved for future use

const saveInputConfig = (inputId: string) => {
  emit('save-input-config', inputId)
}

// @ts-expect-error - Reserved for future use

const saveInputOptions = (inputId: string) => {
  emit('save-input-options', inputId)
}

// @ts-expect-error - Reserved for future use

const saveInputType = (inputId: string) => {
  emit('save-input-type', inputId)
}

// @ts-expect-error - Reserved for future use

const addOption = (inputId: string) => {
  emit('add-option', inputId)
}

// @ts-expect-error - Reserved for future use

const removeOption = (inputId: string, index: number) => {
  emit('remove-option', inputId, index)
}

// 更新输入ID
const updateInputId = (
  oldInputId: string,
  newInputId: string,
  nodeId: string,
  widgetName: string
) => {
  console.log('[WorkflowAppForm] updateInputId - 开始更新:', {
    oldInputId,
    newInputId,
    nodeId,
    widgetName,
    currentInputs: props.workflowInputs.map((i) => ({
      id: i.id,
      nodeId: i.nodeId,
      widgetName: i.widgetName
    }))
  })

  if (oldInputId === newInputId) {
    console.log('[WorkflowAppForm] updateInputId - ID 未变化，跳过')
    return
  }

  // 检查新ID是否已存在
  const existingInput = props.workflowInputs.find((i) => i.id === newInputId)
  if (existingInput) {
    console.warn('[WorkflowAppForm] updateInputId - 新ID已存在:', newInputId)
    toast.add({
      severity: 'warn',
      summary: t('workflowApps.updateInputIdError'),
      detail: t('workflowApps.inputIdAlreadyExists'),
      life: 3000
    })
    return
  }

  // 更新本地数据
  const input = props.workflowInputs.find((i) => i.id === oldInputId)
  if (input) {
    console.log('[WorkflowAppForm] updateInputId - 找到输入，更新前:', {
      id: input.id,
      nodeId: input.nodeId,
      widgetName: input.widgetName
    })

    // 更新 input.id、nodeId 和 widgetName
    input.id = newInputId
    input.nodeId = nodeId
    input.widgetName = widgetName

    console.log('[WorkflowAppForm] updateInputId - 更新后:', {
      id: input.id,
      nodeId: input.nodeId,
      widgetName: input.widgetName,
      allInputs: props.workflowInputs.map((i) => ({
        id: i.id,
        nodeId: i.nodeId,
        widgetName: i.widgetName
      }))
    })

    // 如果 inputValues 中有旧ID的值，需要迁移
    if (props.inputValues[oldInputId] !== undefined) {
      emit('input-change', newInputId, props.inputValues[oldInputId])
      emit('input-change', oldInputId, undefined)
    }

    // 如果 fileMetadata 中有旧ID的元数据，需要迁移
    if (fileMetadata.value[oldInputId]) {
      fileMetadata.value[newInputId] = fileMetadata.value[oldInputId]
      delete fileMetadata.value[oldInputId]
    }

    // 如果 uploadProgress 中有旧ID的进度，需要迁移
    if (uploadProgress.value[oldInputId] !== undefined) {
      uploadProgress.value[newInputId] = uploadProgress.value[oldInputId]
      delete uploadProgress.value[oldInputId]
    }

    // 通知父组件ID已更新（包含 nodeId 和 widgetName）
    emit('update-input-id', oldInputId, newInputId, nodeId, widgetName)
    emit('save-input-config', newInputId)

    toast.add({
      severity: 'success',
      summary: t('workflowApps.updateInputIdSuccess'),
      detail: t('workflowApps.inputIdUpdated'),
      life: 2000
    })
  } else {
    console.error('[WorkflowAppForm] updateInputId - 未找到输入:', oldInputId)
  }
}

// 更新输入配置（从侧边栏同步）
const updateInputConfig = (inputId: string, property: string, value: any) => {
  console.log('更新输入配置:', inputId, property, value)

  // 更新本地数据
  const input = props.workflowInputs.find((i) => i.id === inputId)
  if (input) {
    // 支持嵌套属性，如 'config.min'
    if (property.includes('.')) {
      const [parent, child] = property.split('.')
      if (!(input as any)[parent]) {
        ;(input as any)[parent] = {}
      }
      // 直接赋值
      ;(input as any)[parent][child] = value
      // 如果是 config 对象，重新赋值整个对象以触发响应式更新
      if (parent === 'config') {
        ;(input as any)[parent] = { ...(input as any)[parent] }
      }
    } else {
      ;(input as any)[property] = value

      // 如果类型改变，根据新类型转换值
      if (property === 'type') {
        const oldValue = input.value
        let newValue = oldValue

        // 根据新类型转换值
        switch (value) {
          case 'number':
          case 'int':
          case 'float':
            newValue =
              typeof oldValue === 'number'
                ? oldValue
                : parseFloat(oldValue) || 0
            break
          case 'boolean':
            newValue =
              typeof oldValue === 'boolean' ? oldValue : Boolean(oldValue)
            break
          case 'text':
          case 'string':
          case 'customtext':
          case 'multiline':
          case 'combo':
          case 'file.image':
          case 'file.audio':
          case 'file.audio.select':
          case 'file.video':
            newValue = String(oldValue || '')
            break
          default:
            newValue = oldValue
        }

        input.value = newValue
        console.log('类型转换:', oldValue, '->', newValue)

        // 通知父组件类型已更新
        emit('save-input-type', inputId)
        console.log('已通知父组件类型更新:', inputId, value)
      }
    }

    // 通知父组件配置已更新
    emit('save-input-config', inputId)
    console.log('已通知父组件配置更新:', inputId, property, value)
  }
}

// 获取所有输入项的排序列表（按当前order排序）
const getSortedInputs = () => {
  return [...props.workflowInputs].sort((a, b) => {
    const orderA = a.config?.order ?? a.order ?? 0
    const orderB = b.config?.order ?? b.order ?? 0
    return orderA - orderB
  })
}

// 检查是否可以上移
const canMoveUp = (inputId: string): boolean => {
  const sortedInputs = getSortedInputs()
  const currentIndex = sortedInputs.findIndex((i) => i.id === inputId)
  return currentIndex > 0
}

// 检查是否可以下移
const canMoveDown = (inputId: string): boolean => {
  const sortedInputs = getSortedInputs()
  const currentIndex = sortedInputs.findIndex((i) => i.id === inputId)
  return currentIndex >= 0 && currentIndex < sortedInputs.length - 1
}

// 上移输入项
const moveInputUp = (inputId: string) => {
  const sortedInputs = getSortedInputs()
  const currentIndex = sortedInputs.findIndex((i) => i.id === inputId)
  if (currentIndex <= 0) return

  const currentInput = sortedInputs[currentIndex]
  const prevInput = sortedInputs[currentIndex - 1]

  // 从原始数组中找到对应的输入项
  const currentInputInProps = props.workflowInputs.find(
    (i) => i.id === currentInput.id
  )
  const prevInputInProps = props.workflowInputs.find(
    (i) => i.id === prevInput.id
  )

  if (!currentInputInProps || !prevInputInProps) return

  const currentOrder =
    currentInputInProps.config?.order ?? currentInputInProps.order ?? 0
  const prevOrder =
    prevInputInProps.config?.order ?? prevInputInProps.order ?? 0

  // 使用 updateInputConfig 方法来更新，确保响应式更新
  // 先更新前一个，再更新当前，避免中间状态
  updateInputConfig(prevInputInProps.id, 'config.order', currentOrder)
  updateInputConfig(currentInputInProps.id, 'config.order', prevOrder)
}

// 下移输入项
const moveInputDown = (inputId: string) => {
  const sortedInputs = getSortedInputs()
  const currentIndex = sortedInputs.findIndex((i) => i.id === inputId)
  if (currentIndex < 0 || currentIndex >= sortedInputs.length - 1) return

  const currentInput = sortedInputs[currentIndex]
  const nextInput = sortedInputs[currentIndex + 1]

  // 从原始数组中找到对应的输入项
  const currentInputInProps = props.workflowInputs.find(
    (i) => i.id === currentInput.id
  )
  const nextInputInProps = props.workflowInputs.find(
    (i) => i.id === nextInput.id
  )

  if (!currentInputInProps || !nextInputInProps) return

  const currentOrder =
    currentInputInProps.config?.order ?? currentInputInProps.order ?? 0
  const nextOrder =
    nextInputInProps.config?.order ?? nextInputInProps.order ?? 0

  // 使用 updateInputConfig 方法来更新，确保响应式更新
  // 先更新下一个，再更新当前，避免中间状态
  updateInputConfig(nextInputInProps.id, 'config.order', currentOrder)
  updateInputConfig(currentInputInProps.id, 'config.order', nextOrder)
}

// 自动填充序号（如果所有序号都是0）
const autoFillOrder = () => {
  const allOrders = props.workflowInputs.map(
    (input) => input.config?.order ?? input.order ?? 0
  )

  // 检查是否所有序号都是0
  const allZero = allOrders.every((order) => order === 0)

  if (allZero && props.workflowInputs.length > 0) {
    // 按照当前顺序填充1,2,3,4,5...
    props.workflowInputs.forEach((input, index) => {
      if (!input.config) {
        input.config = {}
      }
      input.config.order = index + 1
      emit('save-input-config', input.id)
    })
  }
}

// 根据 filename 获取 obs_url，用于节点预览直接使用 OBS URL 而非 view 代理
async function fetchObsUrlForInputs() {
  const imageInputs = props.workflowInputs.filter(
    (i) => i.type === 'file.image'
  )
  for (const input of imageInputs) {
    const filename = props.inputValues[input.id]
    if (!filename || typeof filename !== 'string') continue
    const actualFilename = filename.includes('/')
      ? filename.split('/').pop() || filename
      : filename
    if (
      actualFilename.startsWith('http://') ||
      actualFilename.startsWith('https://')
    )
      continue
    if (fileMetadata.value[input.id]?.obsUrl) continue
    if (obsUrlFetchAttempted.has(actualFilename)) continue
    obsUrlFetchAttempted.add(actualFilename)
    try {
      const url = `${FASTAPI_CONFIG.BASE_URL}/api/v1/comfyui/file-obs-url?filename=${encodeURIComponent(actualFilename)}&type=input`
      const res = await fetch(url, { credentials: 'include' })
      if (res.ok) {
        const data = (await res.json()) as { obs_url?: string }
        if (data?.obs_url) {
          mergeFileMetadata(input.id, { obsUrl: data.obs_url })
          imageUrlCache.set(`${input.id}_${filename}`, data.obs_url)
        }
      }
    } catch {
      // 忽略错误，继续使用 view 代理
    }
  }
}

// 监听 inputValues，当有图片输入但无 obsUrl 时拉取 obs_url
watch(
  () => [props.inputValues, props.workflowInputs] as const,
  () => {
    void fetchObsUrlForInputs()
  },
  { immediate: true, deep: true }
)

// 监听编辑模式打开，自动填充序号
watch(
  () => props.isEditMode,
  (isEditMode) => {
    if (isEditMode) {
      // 延迟执行，确保数据已加载
      setTimeout(() => {
        autoFillOrder()
      }, 100)
    }
  }
)

// 添加Combo选项（含 file.audio.select 的预设选项）
const addComboOption = (inputId: string) => {
  console.log('添加Combo选项:', inputId)

  const input = props.workflowInputs.find((i) => i.id === inputId)
  if (input && (input.type === 'combo' || input.type === 'file.audio.select')) {
    if (!input.options) {
      input.options = []
    }

    const newOption =
      input.type === 'file.audio.select'
        ? {
            label: t('workflowApps.audioPreset'),
            value: ''
          }
        : {
            label: t('workflowApps.newOption'),
            value: t('workflowApps.newOptionValue')
          }

    input.options.push(newOption)
    console.log('新选项已添加:', newOption)

    // 通知父组件选项已更新
    emit('save-input-config', inputId)

    // 自动检测类型变化
    autoDetectType(inputId)
  }
}

// 检查选项是否包含图片文件
const hasImageFiles = (options: any): boolean => {
  if (!options) return false
  const optionArray = Array.isArray(options) ? options : [options]
  return optionArray.some((option) => {
    const value = typeof option === 'string' ? option : option.value
    return value && /\.(png|jpg|jpeg|gif|bmp|webp|svg)$/i.test(value)
  })
}

// 自动检测并更新类型
const autoDetectType = (inputId: string) => {
  const input = props.workflowInputs.find((i) => i.id === inputId)
  if (input && input.options) {
    // 如果类型已经是手动设置的文件类型（file.image、file.video、file.audio），不进行自动检测
    // 避免覆盖用户手动设置的类型
    if (
      input.type === 'file.image' ||
      input.type === 'file.video' ||
      input.type === 'file.audio' ||
      input.type === 'file.audio.select'
    ) {
      console.log('输入框类型已是文件类型，跳过自动检测:', inputId, input.type)
      return
    }

    const shouldBeFileImage = hasImageFiles(input.options)
    // 只处理 combo 类型的自动检测
    if (input.type === 'combo') {
      if (shouldBeFileImage) {
        console.log('自动检测到图片文件，更新类型为 file.image:', inputId)
        input.type = 'file.image'

        // 通知父组件类型已更新
        emit('save-input-type', inputId)
        console.log('已通知父组件自动类型更新:', inputId)
      }
    } else if (!shouldBeFileImage && input.type === 'file.image') {
      // 这个分支理论上不应该执行，因为上面已经排除了 file.image
      console.log('未检测到图片文件，恢复类型为 combo:', inputId)
      input.type = 'combo'

      // 通知父组件类型已更新
      emit('save-input-type', inputId)
      console.log('已通知父组件自动类型恢复:', inputId)
    }
  }
}

// 更新Combo选项
const updateComboOption = (
  inputId: string,
  index: number,
  property: 'label' | 'value',
  value: string
) => {
  console.log('更新Combo选项:', inputId, index, property, value)

  const input = props.workflowInputs.find((i) => i.id === inputId)
  if (
    input &&
    (input.type === 'combo' || input.type === 'file.audio.select') &&
    input.options &&
    input.options[index]
  ) {
    input.options[index][property] = value
    console.log('选项已更新:', input.options[index])

    // 通知父组件选项已更新
    emit('save-input-config', inputId)

    // 仅 combo 类型需要自动检测
    if (input.type === 'combo') {
      autoDetectType(inputId)
    }
  }
}

// 删除Combo选项（含 file.audio.select 的预设选项）
const removeComboOption = (inputId: string, index: number) => {
  console.log('删除Combo选项:', inputId, index)

  const input = props.workflowInputs.find((i) => i.id === inputId)
  if (
    input &&
    (input.type === 'combo' || input.type === 'file.audio.select') &&
    input.options &&
    input.options.length > index
  ) {
    input.options.splice(index, 1)
    console.log('选项已删除，剩余选项:', input.options)

    // 通知父组件选项已更新
    emit('save-input-config', inputId)

    // 仅 combo 类型需要自动检测
    if (input.type === 'combo') {
      autoDetectType(inputId)
    }
  }
}

// 处理音频裁剪确认
const handleTrimConfirm = async (blob: Blob, trimmedDuration: number) => {
  if (!currentInputId.value) return

  try {
    // 音频最大时长校验（使用裁剪后的时长）
    const input = props.workflowInputs.find(
      (i) => i.id === currentInputId.value
    )
    const maxDuration = input?.config?.maxDuration
    if (maxDuration && trimmedDuration > maxDuration) {
      toast.add({
        severity: 'warn',
        summary: t('g.warning'),
        detail: t('validation.maxDuration', { duration: maxDuration }),
        life: 5000
      })
      return
    }

    // 创建File对象
    const filename = `trimmed-${Date.now()}.wav`
    const file = new File([blob], filename, { type: 'audio/wav' })

    // 上传裁剪后的音频
    await uploadFile(file, currentInputId.value)

    // 关闭裁剪弹窗
    showAudioTrimmer.value = false

    toast.add({
      severity: 'success',
      summary: t('workflowApps.trimSuccess'),
      detail: t('workflowApps.audioTrimmed'),
      life: 3000
    })
  } catch (error) {
    console.error('上传裁剪后的音频失败:', error)

    // 如果是后端返回的时长超限错误，优先给出更友好的提示
    if (
      error instanceof Error &&
      (error.message.includes('exceeds maximum allowed duration') ||
        error.message.includes('maxDuration') ||
        error.message.includes('duration'))
    ) {
      const input = props.workflowInputs.find(
        (i) => i.id === currentInputId.value
      )
      const maxDuration = input?.config?.maxDuration

      toast.add({
        severity: 'warn',
        summary: t('g.warning'),
        detail: t('validation.maxDuration', { duration: maxDuration || 0 }),
        life: 5000
      })
    } else {
      // 其他错误使用通用上传失败提示
      toast.add({
        severity: 'error',
        summary: t('workflowApps.uploadError'),
        detail: t('workflowApps.uploadAudioFailed'),
        life: 5000
      })
    }
  } finally {
    // 清理临时URL和原始文件引用
    if (trimAudioUrl.value && trimAudioUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(trimAudioUrl.value)
    }
    trimAudioUrl.value = ''
    originalAudioFile.value = null
  }
}

// 不裁剪 - 上传原音频
const handleNoTrim = async () => {
  // 如果没有原始文件（从服务器加载的音频），直接关闭弹窗
  if (!originalAudioFile.value) {
    if (trimAudioUrl.value && trimAudioUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(trimAudioUrl.value)
    }
    trimAudioUrl.value = ''
    showAudioTrimmer.value = false
    return
  }

  // 如果有原始文件，上传原音频
  if (!currentInputId.value) {
    showAudioTrimmer.value = false
    return
  }

  try {
    // 音频最大时长校验
    const input = props.workflowInputs.find(
      (i) => i.id === currentInputId.value
    )
    const maxDuration = input?.config?.maxDuration
    if (maxDuration && originalAudioFile.value) {
      try {
        const duration = await getAudioDuration(originalAudioFile.value)
        if (duration > maxDuration) {
          toast.add({
            severity: 'warn',
            summary: t('g.warning'),
            detail: t('validation.maxDuration', { duration: maxDuration }),
            life: 5000
          })
          return
        }
      } catch (error) {
        console.warn('获取音频时长失败:', error)
        // 如果获取时长失败，仍然允许上传，由后端验证
      }
    }

    // 上传原始音频
    await uploadFile(originalAudioFile.value, currentInputId.value)

    // 关闭裁剪弹窗
    showAudioTrimmer.value = false
  } catch (error) {
    console.error('上传原音频失败:', error)

    // 如果是后端返回的时长超限错误，优先给出更友好的提示
    if (
      error instanceof Error &&
      (error.message.includes('exceeds maximum allowed duration') ||
        error.message.includes('maxDuration') ||
        error.message.includes('duration'))
    ) {
      const input = props.workflowInputs.find(
        (i) => i.id === currentInputId.value
      )
      const maxDuration = input?.config?.maxDuration

      toast.add({
        severity: 'warn',
        summary: t('g.warning'),
        detail: t('validation.maxDuration', { duration: maxDuration || 0 }),
        life: 5000
      })
    } else {
      // 其他错误使用通用上传失败提示
      toast.add({
        severity: 'error',
        summary: t('workflowApps.uploadError'),
        detail: t('workflowApps.uploadAudioFailed'),
        life: 5000
      })
    }
  } finally {
    // 清理临时URL和原始文件引用
    if (trimAudioUrl.value && trimAudioUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(trimAudioUrl.value)
    }
    trimAudioUrl.value = ''
    originalAudioFile.value = null
  }
}

// 取消裁剪 - 关闭弹窗，不上传
const handleTrimCancel = () => {
  // 清理临时URL和原始文件引用
  if (trimAudioUrl.value && trimAudioUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(trimAudioUrl.value)
  }
  trimAudioUrl.value = ''
  originalAudioFile.value = null
  showAudioTrimmer.value = false
}

const cleanupPreviewUrl = (url?: string) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

const setFileMetadata = (inputId: string, metadata: FileMetadataItem) => {
  fileMetadata.value = {
    ...fileMetadata.value,
    [inputId]: metadata
  }
}

const mergeFileMetadata = (
  inputId: string,
  metadata: Partial<FileMetadataItem>
) => {
  setFileMetadata(inputId, {
    ...(fileMetadata.value[inputId] || {}),
    ...metadata
  })
}

// 统一去掉前缀目录，返回纯文件名
const getBasename = (path: string) => {
  if (!path) return ''
  const sanitized = path.split(/[?#]/)[0]
  const normalized = sanitized.replaceAll('\\', '/')
  const parts = normalized.split('/')
  return parts[parts.length - 1] || normalized
}

// 处理视频帧选择确认
const handleVideoFrameConfirm = async (blob: Blob) => {
  if (!currentInputId.value) return

  try {
    // 创建File对象
    const filename = `video-frame-${Date.now()}.jpg`
    const file = new File([blob], filename, { type: 'image/jpeg' })

    // 使用带进度的上传函数
    await uploadFile(file, currentInputId.value, 'workflow-apps')

    // 关闭视频帧选择弹窗
    showVideoFrameSelector.value = false

    toast.add({
      severity: 'success',
      summary: t('workflowApps.frameExtracted'),
      detail: t('workflowApps.frameExtractedSuccess'),
      life: 3000
    })
  } catch (error) {
    console.error('上传视频帧失败:', error)
    toast.add({
      severity: 'error',
      summary: t('workflowApps.uploadError'),
      detail: t('workflowApps.uploadFrameFailed'),
      life: 5000
    })
  } finally {
    // 清理临时URL和视频文件引用
    if (selectedVideoUrl.value && selectedVideoUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(selectedVideoUrl.value)
    }
    selectedVideoUrl.value = ''
    selectedVideoFile.value = null
  }
}

// 处理视频帧选择取消
const handleVideoFrameCancel = () => {
  // 清理临时URL和视频文件引用
  if (selectedVideoUrl.value && selectedVideoUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(selectedVideoUrl.value)
  }
  selectedVideoUrl.value = ''
  selectedVideoFile.value = null
  showVideoFrameSelector.value = false
}

// 处理视频裁剪确认
const handleVideoTrimConfirm = async (blob: Blob, trimmedDuration: number) => {
  if (!currentInputId.value) return

  try {
    const input = props.workflowInputs.find(
      (i) => i.id === currentInputId.value
    )
    const maxDuration = input?.config?.maxDuration
    if (maxDuration && trimmedDuration > maxDuration) {
      toast.add({
        severity: 'warn',
        summary: t('g.warning'),
        detail: t('validation.maxDuration', { duration: maxDuration }),
        life: 5000
      })
      return
    }

    const filename = `trimmed-video-${Date.now()}.webm`
    const file = new File([blob], filename, { type: 'video/webm' })
    await uploadFile(file, currentInputId.value)

    showVideoTrimmer.value = false
    toast.add({
      severity: 'success',
      summary: t('workflowApps.trimSuccess'),
      detail: t('workflowApps.videoTrimmed'),
      life: 3000
    })
  } catch (error) {
    console.error('上传裁剪后的视频失败:', error)
    toast.add({
      severity: 'error',
      summary: t('workflowApps.uploadError'),
      detail: t('workflowApps.uploadVideoFailed'),
      life: 5000
    })
  } finally {
    if (trimVideoUrl.value && trimVideoUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(trimVideoUrl.value)
    }
    trimVideoUrl.value = ''
    originalVideoFile.value = null
  }
}

// 不裁剪 - 上传原视频
const handleVideoNoTrim = async () => {
  // 先关闭弹窗，避免等待上传
  showVideoTrimmer.value = false

  try {
    if (!originalVideoFile.value || !currentInputId.value) {
      return
    }

    const input = props.workflowInputs.find(
      (i) => i.id === currentInputId.value
    )
    const maxDuration = input?.config?.maxDuration
    if (maxDuration) {
      try {
        const duration = await getVideoDuration(originalVideoFile.value)
        if (duration > maxDuration) {
          toast.add({
            severity: 'warn',
            summary: t('g.warning'),
            detail: t('validation.maxDuration', { duration: maxDuration }),
            life: 5000
          })
          return
        }
      } catch (error) {
        console.warn('获取视频时长失败:', error)
      }
    }

    await uploadFile(originalVideoFile.value, currentInputId.value)
  } catch (error) {
    console.error('上传原视频失败:', error)
    toast.add({
      severity: 'error',
      summary: t('workflowApps.uploadError'),
      detail: t('workflowApps.uploadVideoFailed'),
      life: 5000
    })
  } finally {
    if (trimVideoUrl.value && trimVideoUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(trimVideoUrl.value)
    }
    trimVideoUrl.value = ''
    originalVideoFile.value = null
  }
}

// 取消视频裁剪
const handleVideoTrimCancel = () => {
  if (trimVideoUrl.value && trimVideoUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(trimVideoUrl.value)
  }
  trimVideoUrl.value = ''
  originalVideoFile.value = null
  showVideoTrimmer.value = false
}

// 优化提示词
const optimizePrompt = async (inputId: string, isRegenerate = false) => {
  const input = props.workflowInputs.find((i) => i.id === inputId)
  if (!input) {
    return
  }

  const currentPrompt = props.inputValues[inputId] || ''
  if (!currentPrompt || !String(currentPrompt).trim()) {
    toast.add({
      severity: 'warn',
      summary: t('workflowApps.optimizePromptError'),
      detail: t('workflowApps.promptEmpty'),
      life: 3000
    })
    return
  }

  try {
    isOptimizing.value = true
    currentOptimizingInputId.value = inputId

    // 如果是第一次优化，保存原始提示词
    if (!isRegenerate && !originalPrompts.value[inputId]) {
      originalPrompts.value[inputId] = String(currentPrompt).trim()
    }

    // 如果是重新生成，使用原始提示词；否则使用当前提示词
    const promptToOptimize =
      isRegenerate && originalPrompts.value[inputId]
        ? originalPrompts.value[inputId]
        : String(currentPrompt).trim()

    // 收集所有图片输入的URL
    const imageUrls: string[] = []
    const imageInputs = props.workflowInputs.filter(
      (i) =>
        i.type === 'file.image' && i.id !== inputId && props.inputValues[i.id]
    )

    for (const imageInput of imageInputs) {
      const imageValue = props.inputValues[imageInput.id]
      if (!imageValue) continue

      try {
        // 获取原始图片URL（不使用代理，直接传给后端）
        const imageUrl = getOriginalImageUrl(String(imageValue), imageInput.id)
        if (imageUrl) {
          imageUrls.push(imageUrl)
        }
      } catch (error) {
        console.warn(`获取图片 ${imageInput.id} 失败:`, error)
        // 继续处理其他图片，不中断流程
      }
    }

    // 构建请求数据
    const requestData = {
      prompt: promptToOptimize,
      input_id: inputId,
      share_id: props.shareId ? String(props.shareId) : null,
      image_urls: imageUrls.length > 0 ? imageUrls : null
    }

    // 获取认证token
    const token = await api.getAuthToken()

    const response = await fetch(
      `${FASTAPI_CONFIG.BASE_URL}/api/prompt-optimize/optimize`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Comfy-User': api.user || '',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(requestData)
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `HTTP ${response.status}`)
    }

    const result = await response.json()

    if (result.success && result.data?.optimized_prompt) {
      // 将优化后的提示词填充到输入框
      emit('input-change', inputId, result.data.optimized_prompt)

      // 标记为已优化
      optimizedInputIds.value.add(inputId)

      toast.add({
        severity: 'success',
        summary: isRegenerate
          ? t('workflowApps.regeneratePromptSuccess')
          : t('workflowApps.optimizePromptSuccess'),
        detail: isRegenerate
          ? t('workflowApps.promptRegenerated')
          : t('workflowApps.promptOptimized'),
        life: 3000
      })
    } else {
      throw new Error(result.message || '优化失败')
    }
  } catch (error: any) {
    console.error('优化提示词失败:', error)
    toast.add({
      severity: 'error',
      summary: t('workflowApps.optimizePromptError'),
      detail: error.message || t('workflowApps.optimizePromptFailed'),
      life: 5000
    })
  } finally {
    isOptimizing.value = false
    currentOptimizingInputId.value = ''
  }
}

// 撤回提示词（恢复到优化前的原始提示词）
const revertPrompt = (inputId: string) => {
  if (originalPrompts.value[inputId]) {
    emit('input-change', inputId, originalPrompts.value[inputId])
    optimizedInputIds.value.delete(inputId)
    delete originalPrompts.value[inputId]

    toast.add({
      severity: 'success',
      summary: t('workflowApps.revertPromptSuccess'),
      detail: t('workflowApps.promptReverted'),
      life: 3000
    })
  }
}

// 暴露方法及状态供父组件调用
defineExpose({
  setFileMetadata,
  hasUploadingFiles
})

onUnmounted(() => {
  Object.values(fileMetadata.value).forEach((meta) => {
    cleanupPreviewUrl(meta.previewUrl)
  })

  // 清理视频URL
  if (selectedVideoUrl.value && selectedVideoUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(selectedVideoUrl.value)
  }
})
</script>

<style scoped>
.workflow-app-form {
  position: relative;
  background-color: var(--p-surface-card);
  min-height: 6rem;
}

.workflow-input-item {
  transition: all 0.2s ease;
}

.workflow-input-item:hover {
  border-color: var(--p-primary-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.workflow-input-control :deep(.p-inputtext),
.workflow-input-control :deep(.p-inputnumber),
.workflow-input-control :deep(.p-select),
.workflow-input-control :deep(.p-textarea) {
  width: 100%;
}

/* 使用项目现有的CSS变量系统 */
.workflow-input-item {
  background-color: var(--comfy-menu-secondary-bg);
  color: var(--p-text-color, #fff);
}

.workflow-input-item h4 {
  color: var(--p-text-color, #fff);
}

.workflow-input-item .text-xs {
  color: var(--p-text-muted-color, #9ca3af);
}

.workflow-input-item .bg-surface-100 {
  background-color: var(--comfy-menu-secondary-bg);
  color: var(--p-text-color, #fff);
}

/* 强制覆盖Tailwind类名，使用项目变量 */
.workflow-input-item.bg-surface-0 {
  background-color: var(--comfy-menu-secondary-bg) !important;
}

.workflow-input-item.border-surface-200 {
  border-color: var(--border-color) !important;
}

.workflow-input-item .text-surface-900 {
  color: var(--p-text-color, #fff) !important;
}

.workflow-input-item .text-surface-500 {
  color: var(--p-text-muted-color, #9ca3af) !important;
}

.workflow-input-item .text-surface-700 {
  color: var(--p-text-color, #fff) !important;
}

/* 表单输入框样式，使用项目CSS变量 */
.workflow-input-field :deep(.p-inputtext),
.workflow-input-field :deep(.p-inputnumber input),
.workflow-input-field :deep(.p-select .p-select-label),
.workflow-input-field :deep(.p-textarea) {
  background-color: var(--comfy-menu-secondary-bg);
  border-color: var(--border-color);
  color: var(--p-text-color, #fff);
}

.workflow-input-field :deep(.p-inputtext:focus),
.workflow-input-field :deep(.p-inputnumber input:focus),
.workflow-input-field :deep(.p-select .p-select-label:focus),
.workflow-input-field :deep(.p-textarea:focus) {
  border-color: var(--p-primary-color);
  box-shadow: 0 0 0 1px var(--p-primary-color);
}

.workflow-input-field :deep(.p-inputtext::placeholder),
.workflow-input-field :deep(.p-textarea::placeholder) {
  color: var(--p-text-muted-color, #9ca3af);
}

/* 下拉菜单样式 */
.workflow-input-field :deep(.p-select-dropdown) {
  background-color: var(--comfy-menu-secondary-bg);
  border-color: var(--border-color);
}

.workflow-input-field :deep(.p-select-option) {
  color: var(--p-text-color, #fff);
}

.workflow-input-field :deep(.p-select-option:hover) {
  background-color: var(--comfy-menu-hover-bg);
}

/* 切换开关样式 */
.workflow-input-field :deep(.p-toggleswitch) {
  background-color: var(--border-color);
}

.workflow-input-field :deep(.p-toggleswitch.p-toggleswitch-checked) {
  background-color: var(--p-primary-color);
}

/* 操作按钮样式 */
.delete-button {
  padding: 4px !important;
  min-width: 24px !important;
  height: 24px !important;
  border-radius: 4px !important;
}

/* 删除按钮悬停样式 */
.delete-button:hover {
  background-color: var(--p-red-100) !important;
  border-radius: 4px !important;
}

/* 调整表单高度 */
.workflow-input-control :deep(.p-inputtext),
.workflow-input-control :deep(.p-inputnumber input),
.workflow-input-control :deep(.p-select .p-select-label) {
  height: 32px !important;
  min-height: 32px !important;
}

/* Textarea 自动高度 */
.workflow-input-control :deep(.p-textarea) {
  min-height: 32px !important;
  max-height: 300px;
  resize: vertical;
  overflow-y: auto;
  line-height: 1.5;
  padding: 6px 12px;
}

/* 移除h4标签的上下margin */
.workflow-input-item h4 {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

/* 图片选择按钮样式 - 蓝色 */
.select-image-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border: none !important;
  color: white !important;
}

.select-image-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%) !important;
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3) !important;
  color: white !important;
}

.select-image-btn :deep(.p-button-label),
.select-image-btn :deep(.p-button-icon),
.select-image-btn :deep(.p-button-icon-left),
.select-image-btn :deep(.p-button-icon-right) {
  color: white !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.select-image-btn:hover:not(:disabled) :deep(.p-button-label),
.select-image-btn:hover:not(:disabled) :deep(.p-button-icon),
.select-image-btn:hover:not(:disabled) :deep(.p-button-icon-left),
.select-image-btn:hover:not(:disabled) :deep(.p-button-icon-right) {
  color: white !important;
  opacity: 1 !important;
  visibility: visible !important;
}

/* 图片上传按钮样式 - 绿色 */
.upload-image-btn {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%) !important;
  border: none !important;
  color: white !important;
}

.upload-image-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #0e8478 0%, #2fd46d 100%) !important;
  box-shadow: 0 4px 8px rgba(17, 153, 142, 0.3) !important;
  color: white !important;
}

.upload-image-btn :deep(.p-button-label),
.upload-image-btn :deep(.p-button-icon),
.upload-image-btn :deep(.p-button-icon-left),
.upload-image-btn :deep(.p-button-icon-right) {
  color: white !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.upload-image-btn:hover:not(:disabled) :deep(.p-button-label),
.upload-image-btn:hover:not(:disabled) :deep(.p-button-icon),
.upload-image-btn:hover:not(:disabled) :deep(.p-button-icon-left),
.upload-image-btn:hover:not(:disabled) :deep(.p-button-icon-right) {
  color: white !important;
  opacity: 1 !important;
  visibility: visible !important;
}

/* 图片粘贴按钮样式 - 橙色 */
.paste-image-btn {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%) !important;
  border: none !important;
  color: white !important;
}

.paste-image-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #e67ee8 0%, #e04257 100%) !important;
  box-shadow: 0 4px 8px rgba(240, 147, 251, 0.3) !important;
  color: white !important;
}

.paste-image-btn :deep(.p-button-label),
.paste-image-btn :deep(.p-button-icon),
.paste-image-btn :deep(.p-button-icon-left),
.paste-image-btn :deep(.p-button-icon-right) {
  color: white !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.paste-image-btn:hover:not(:disabled) :deep(.p-button-label),
.paste-image-btn:hover:not(:disabled) :deep(.p-button-icon),
.paste-image-btn:hover:not(:disabled) :deep(.p-button-icon-left),
.paste-image-btn:hover:not(:disabled) :deep(.p-button-icon-right) {
  color: white !important;
  opacity: 1 !important;
  visibility: visible !important;
}

/* 禁用状态 */
.select-image-btn:disabled,
.upload-image-btn:disabled,
.paste-image-btn:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* 录音按钮样式 - 蓝色 */
.record-audio-btn {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%) !important;
  border: none !important;
  color: white !important;
  transition: all 0.3s ease;
}

/* 优化提示词按钮样式 - 橙色主题渐变效果 */
.optimize-prompt-btn {
  background: linear-gradient(
    135deg,
    #ff6b35 0%,
    #f7931e 50%,
    #ff8c42 100%
  ) !important;
  border: none !important;
  color: white !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 2px 8px rgba(255, 107, 53, 0.25),
    0 1px 3px rgba(0, 0, 0, 0.1) !important;
  position: relative;
  overflow: hidden;
}

.optimize-prompt-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s ease;
}

.optimize-prompt-btn:hover:not(:disabled)::before {
  left: 100%;
}

.optimize-prompt-btn:hover:not(:disabled) {
  background: linear-gradient(
    135deg,
    #e55a2b 0%,
    #e0841a 50%,
    #ff7a35 100%
  ) !important;
  box-shadow:
    0 4px 16px rgba(255, 107, 53, 0.4),
    0 2px 8px rgba(247, 147, 30, 0.3) !important;
  transform: translateY(-1px);
  color: white !important;
}

.optimize-prompt-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow:
    0 2px 8px rgba(255, 107, 53, 0.3),
    0 1px 4px rgba(247, 147, 30, 0.2) !important;
}

.optimize-prompt-btn :deep(.p-button-label),
.optimize-prompt-btn :deep(.p-button-icon),
.optimize-prompt-btn :deep(.p-button-icon-left),
.optimize-prompt-btn :deep(.p-button-icon-right) {
  color: white !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.optimize-prompt-btn:hover:not(:disabled) :deep(.p-button-label),
.optimize-prompt-btn:hover:not(:disabled) :deep(.p-button-icon),
.optimize-prompt-btn:hover:not(:disabled) :deep(.p-button-icon-left),
.optimize-prompt-btn:hover:not(:disabled) :deep(.p-button-icon-right) {
  color: white !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.optimize-prompt-btn:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

.record-audio-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #3d8bfe 0%, #00d9fe 100%) !important;
  box-shadow: 0 4px 8px rgba(79, 172, 254, 0.3) !important;
  color: white !important;
}

.record-audio-btn :deep(.p-button-label),
.record-audio-btn :deep(.p-button-icon),
.record-audio-btn :deep(.p-button-icon-left),
.record-audio-btn :deep(.p-button-icon-right) {
  color: white !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.record-audio-btn:hover:not(:disabled) :deep(.p-button-label),
.record-audio-btn:hover:not(:disabled) :deep(.p-button-icon),
.record-audio-btn:hover:not(:disabled) :deep(.p-button-icon-left),
.record-audio-btn:hover:not(:disabled) :deep(.p-button-icon-right) {
  color: white !important;
  opacity: 1 !important;
  visibility: visible !important;
}

/* 录音中的脉冲动画 */
.record-audio-btn.recording-pulse {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}

.record-audio-btn:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* 裁剪、遮罩、裁剪音频按钮样式 - 灰色背景 */
.crop-mask-btn {
  background-color: #6b7280 !important;
  border-color: #6b7280 !important;
  color: white !important;
}

.crop-mask-btn:hover:not(:disabled) {
  background-color: #4b5563 !important;
  border-color: #4b5563 !important;
  color: white !important;
}

.crop-mask-btn :deep(.p-button-label),
.crop-mask-btn :deep(.p-button-icon),
.crop-mask-btn :deep(.p-button-icon-left),
.crop-mask-btn :deep(.p-button-icon-right) {
  color: white !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.crop-mask-btn:hover:not(:disabled) :deep(.p-button-label),
.crop-mask-btn:hover:not(:disabled) :deep(.p-button-icon),
.crop-mask-btn:hover:not(:disabled) :deep(.p-button-icon-left),
.crop-mask-btn:hover:not(:disabled) :deep(.p-button-icon-right) {
  color: white !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.crop-mask-btn:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* 图片预览样式优化 */
.file-preview-container {
  position: relative;
  border: 1px dashed var(--border-color);
  border-radius: 0.75rem;
  padding: 0.75rem;
  background-color: rgba(var(--comfy-menu-secondary-bg-rgb, 42, 42, 42), 0.35);
  width: 100%;
  aspect-ratio: 1;
}

.image-preview {
  display: block;
  position: relative;
  width: 100%;
  height: 100%;
}

.audio-preview {
  position: relative;
  width: 100%;
}

.video-preview {
  position: relative;
  width: 100%;
}

.video-player {
  width: 100%;
  max-height: 240px;
  border-radius: 0.75rem;
  border: 1px solid var(--border-color);
  background-color: #000;
  object-fit: contain;
}

/* 音频预览容器高度自适应 */
.file-preview-container.audio-container {
  min-height: auto;
  height: auto;
  aspect-ratio: auto;
}

.file-preview-container.video-container {
  min-height: auto;
  height: auto;
  aspect-ratio: auto;
}

/* 缩略图样式 */
.image-preview .thumbnail-image {
  width: 100%;
  height: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  max-width: 100%;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.image-preview .thumbnail-image:hover {
  transform: scale(1.02);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* 文件名显示样式 */
.file-name-display {
  padding: 0.5rem;
  background: rgba(var(--comfy-menu-secondary-bg-rgb, 42, 42, 42), 0.5);
  border-radius: 0.375rem;
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  max-width: 100%;
  word-break: break-all;
}

.file-name-display i {
  flex-shrink: 0;
  color: var(--p-primary-color);
}

/* 上传进度条样式 */
.upload-progress-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  border-radius: 0.5rem;
}

.upload-progress-circle {
  --progress: 0;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: conic-gradient(
    #38ef7d calc(var(--progress) * 1deg),
    rgba(255, 255, 255, 0.2) 0
  );
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: 0 0 20px rgba(17, 153, 142, 0.35);
}

.upload-progress-circle::before {
  content: '';
  position: absolute;
  inset: 6px;
  background: rgba(10, 10, 10, 0.85);
  border-radius: 50%;
}

.upload-progress-circle span {
  position: relative;
  font-size: 0.9rem;
  font-weight: 600;
  color: #ffffff;
}

/* 拖拽上传样式 - 应用到卡片上 */
.workflow-input-item.drag-over {
  border-color: var(--p-primary-color) !important;
  background-color: rgba(
    var(--p-primary-color-rgb, 102, 126, 234),
    0.1
  ) !important;
  transform: scale(1.01);
  box-shadow: 0 4px 12px rgba(var(--p-primary-color-rgb, 102, 126, 234), 0.2) !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.uploading-overlay {
  position: absolute;
  inset: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 0.75rem;
  text-align: center;
  color: #fff;
  backdrop-filter: blur(2px);
  pointer-events: none;
}

.uploading-overlay__spinner {
  width: 2.5rem;
  height: 2.5rem;
}

.uploading-overlay__spinner :deep(svg) {
  width: 100%;
  height: 100%;
}

.uploading-overlay__text {
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
}

.uploading-overlay__progress {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  opacity: 0.9;
}

.form-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  border-radius: 0.75rem;
}

.form-loading-spinner {
  width: 3rem;
  height: 3rem;
}

.form-loading-spinner :deep(svg) {
  width: 100%;
  height: 100%;
}

.form-loading-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--p-text-color, #fff);
}

/* 文件上传高级UI样式 */
.file-preview-wrapper {
  position: relative;
  margin-bottom: 0.5rem;
  width: 100%;
}

/* 图片预览的 wrapper 使用 1:1 比例 */
.file-preview-wrapper:has(
    .file-preview-container:not(.audio-container):not(.video-container)
  ) {
  aspect-ratio: 1;
}

/* 文件输入网格布局 */
.file-input-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 140px));
  gap: 0.75rem;
  justify-content: start;
}

/* 单个图片输入也限制最大宽度，使用表格样式 */
.workflow-input-item .file-preview-wrapper:has(.image-preview),
.workflow-input-item .file-preview-wrapper:has(.file-upload-placeholder) {
  max-width: 150px;
}

/* 上传占位符样式 */
.file-upload-placeholder {
  position: relative;
  aspect-ratio: 1;
  width: 100%;
  border: 2px dashed var(--border-color, #6b7280);
  border-radius: 0.75rem;
  background-color: rgba(var(--comfy-menu-secondary-bg-rgb, 42, 42, 42), 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.file-upload-placeholder:hover,
.file-upload-placeholder.has-hover {
  background-color: rgba(107, 114, 128, 0.1); /* 灰色背景 */
  border-color: var(--border-color, #6b7280);
}

.file-upload-placeholder:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.upload-placeholder-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  color: var(--p-text-muted-color, #9ca3af);
  transition: all 0.2s ease;
}

.file-upload-placeholder:hover .upload-placeholder-icon,
.file-upload-placeholder.has-hover .upload-placeholder-icon {
  color: var(--p-text-muted-color, #9ca3af);
}

.upload-placeholder-icon i {
  font-size: 2rem;
}

/* 素材库按钮样式 - 右下角 */
.material-library-btn {
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  z-index: 10;
  background: rgba(0, 0, 0, 0.6) !important;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  transition:
    background 0.3s ease,
    transform 0.3s ease;
  padding: 0.5rem !important;
  min-width: auto !important;
  width: auto !important;
}

.material-library-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.8) !important;
  transform: scale(1.05);
}

.material-library-btn :deep(.p-button-icon) {
  color: white !important;
  margin: 0 !important;
}

.material-library-btn :deep(.p-button-label) {
  display: none !important;
}

/* Tooltip 样式 */
.material-library-btn::after {
  content: attr(title);
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 0.5rem;
  padding: 0.375rem 0.75rem;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(8px);
  color: white;
  font-size: 0.75rem;
  white-space: nowrap;
  border-radius: 0.375rem;
  opacity: 0;
  pointer-events: none;
  transform: translateY(0.25rem);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.material-library-btn:hover::after {
  opacity: 1;
  transform: translateY(0);
}

/* Tooltip 小箭头 */
.material-library-btn::before {
  content: '';
  position: absolute;
  bottom: 100%;
  right: 0.75rem;
  margin-bottom: 0.25rem;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid rgba(0, 0, 0, 0.9);
  opacity: 0;
  pointer-events: none;
  transform: translateY(0.25rem);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  z-index: 1001;
}

.material-library-btn:hover::before {
  opacity: 1;
  transform: translateY(0);
}

/* 预览容器样式优化 */
.file-preview-container {
  position: relative;
  border: none;
  border-radius: 0.75rem;
  padding: 0;
  background-color: transparent;
  width: 100%;
  aspect-ratio: 1;
}

.image-preview {
  display: block;
  position: relative;
  width: 100%;
  height: 100%;
}

.image-preview .thumbnail-image {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  max-width: 150px;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  display: block;
}

.image-preview .thumbnail-image:hover {
  transform: scale(1.02);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* 预览操作按钮 */
.preview-action-btn {
  position: absolute;
  z-index: 10;
  background: rgba(0, 0, 0, 0.7) !important;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease,
    background 0.2s ease;
  opacity: 0;
  pointer-events: none;
}

.image-preview:hover .preview-action-btn,
.audio-preview:hover .preview-action-btn,
.video-preview:hover .preview-action-btn {
  opacity: 1;
  pointer-events: auto;
}

.preview-action-btn:hover {
  background: rgba(0, 0, 0, 0.85) !important;
  transform: scale(1.05);
}

.preview-delete-btn {
  top: -1.5rem;
  right: -0.5rem;
  background: rgba(239, 68, 68, 0.9) !important;
}

.preview-delete-btn:hover:not(:disabled) {
  background: rgba(239, 68, 68, 1) !important;
}

.preview-crop-btn {
  top: -1.25rem;
  right: 2.5rem;
}

.preview-action-btn :deep(.p-button-icon) {
  color: white !important;
  margin: 0 !important;
}

.preview-action-btn :deep(.p-button-label) {
  display: none !important;
}

.preview-action-btn :deep(.p-button) {
  padding: 0.5rem !important;
  min-width: auto !important;
  width: auto !important;
}

/* Tooltip 样式 */
.preview-action-btn::after {
  content: attr(title);
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 0.5rem;
  padding: 0.375rem 0.75rem;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(8px);
  color: white;
  font-size: 0.75rem;
  white-space: nowrap;
  border-radius: 0.375rem;
  opacity: 0;
  pointer-events: none;
  transform: translateY(0.25rem);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.preview-action-btn:hover::after {
  opacity: 1;
  transform: translateY(0);
}

/* Tooltip 小箭头 */
.preview-action-btn::before {
  content: '';
  position: absolute;
  bottom: 100%;
  right: 0.75rem;
  margin-bottom: 0.25rem;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid rgba(0, 0, 0, 0.9);
  opacity: 0;
  pointer-events: none;
  transform: translateY(0.25rem);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  z-index: 1001;
}

.preview-action-btn:hover::before {
  opacity: 1;
  transform: translateY(0);
}

/* 顺序输入框宽度 */
.order-input :deep(.p-inputnumber-input) {
  width: 80px;
}
</style>
